// src/pages/RebarCalculator.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import './RebarCalculator.css';

/* ══════════════════════════════════════════════════════════════════
   STEEL DATA  —  AS/NZS 4671 & InfraBuild ONEMESH® specs
══════════════════════════════════════════════════════════════════ */

const MESH_DATA = {
  SL53:  { series:'SL', wire:'5.0×200 / 3.55×200',   kgm2:2.61,  longD:5.00,  crossD:3.55,  longSp:200, crossSp:200,
    notes:'5.0×200 = Longitudinal: 5.0 mm dia @ 200 mm crs · 3.55×200 = Cross: 3.55 mm dia @ 200 mm crs' },
  SL62:  { series:'SL', wire:'6.0×200 / 4.75×100',   kgm2:3.72,  longD:6.00,  crossD:4.75,  longSp:200, crossSp:100,
    notes:'6.0×200 = Longitudinal: 6.0 mm dia @ 200 mm crs · 4.75×100 = Cross: 4.75 mm dia @ 100 mm crs' },
  SL72:  { series:'SL', wire:'6.75×200 / 4.75×100',  kgm2:4.78,  longD:6.75,  crossD:4.75,  longSp:200, crossSp:100,
    notes:'6.75×200 = Longitudinal: 6.75 mm dia @ 200 mm crs (along sheet length) · 4.75×100 = Cross: 4.75 mm dia @ 100 mm crs (across sheet width)' },
  SL82:  { series:'SL', wire:'7.6×200 / 5.35×100',   kgm2:6.07,  longD:7.60,  crossD:5.35,  longSp:200, crossSp:100,
    notes:'7.6×200 = Longitudinal: 7.6 mm dia @ 200 mm crs (along sheet length) · 5.35×100 = Cross: 5.35 mm dia @ 100 mm crs (across sheet width)' },
  SL92:  { series:'SL', wire:'8.6×200 / 6.0×100',    kgm2:7.77,  longD:8.60,  crossD:6.00,  longSp:200, crossSp:100,
    notes:'8.6×200 = Longitudinal: 8.6 mm dia @ 200 mm crs · 6.0×100 = Cross: 6.0 mm dia @ 100 mm crs' },
  SL102: { series:'SL', wire:'9.5×200 / 6.75×100',   kgm2:9.50,  longD:9.50,  crossD:6.75,  longSp:200, crossSp:100,
    notes:'9.5×200 = Longitudinal: 9.5 mm dia @ 200 mm crs · 6.75×100 = Cross: 6.75 mm dia @ 100 mm crs' },
  SL81:  { series:'SL', wire:'7.6×100 / 5.35×200',   kgm2:5.28,  longD:7.60,  crossD:5.35,  longSp:100, crossSp:200,
    notes:'7.6×100 = Longitudinal: 7.6 mm dia @ 100 mm crs · 5.35×200 = Cross: 5.35 mm dia @ 200 mm crs' },
  RL718: { series:'RL', wire:'6.75×100 / 5.0×200',   kgm2:4.59,  longD:6.75,  crossD:5.00,  longSp:100, crossSp:200,
    notes:'6.75×100 = Longitudinal: 6.75 mm dia @ 100 mm crs (main direction) · 5.0×200 = Cross: 5.0 mm dia @ 200 mm crs' },
  RL818: { series:'RL', wire:'7.6×100 / 5.0×200',    kgm2:5.65,  longD:7.60,  crossD:5.00,  longSp:100, crossSp:200,
    notes:'7.6×100 = Longitudinal: 7.6 mm dia @ 100 mm crs (main direction) · 5.0×200 = Cross: 5.0 mm dia @ 200 mm crs' },
  RL918: { series:'RL', wire:'8.6×100 / 5.0×200',    kgm2:7.07,  longD:8.60,  crossD:5.00,  longSp:100, crossSp:200,
    notes:'8.6×100 = Longitudinal: 8.6 mm dia @ 100 mm crs (main direction) · 5.0×200 = Cross: 5.0 mm dia @ 200 mm crs' },
  RL1018:{ series:'RL', wire:'9.5×100 / 5.0×200',    kgm2:8.56,  longD:9.50,  crossD:5.00,  longSp:100, crossSp:200,
    notes:'9.5×100 = Longitudinal: 9.5 mm dia @ 100 mm crs (main direction) · 5.0×200 = Cross: 5.0 mm dia @ 200 mm crs' },
  RL1118:{ series:'RL', wire:'10.65×100 / 5.0×200',  kgm2:10.45, longD:10.65, crossD:5.00,  longSp:100, crossSp:200,
    notes:'10.65×100 = Longitudinal: 10.65 mm dia @ 100 mm crs (main direction) · 5.0×200 = Cross: 5.0 mm dia @ 200 mm crs' },
  RL1218:{ series:'RL', wire:'11.9×100 / 5.0×200',   kgm2:12.65, longD:11.90, crossD:5.00,  longSp:100, crossSp:200,
    notes:'11.9×100 = Longitudinal: 11.9 mm dia @ 100 mm crs (main direction) · 5.0×200 = Cross: 5.0 mm dia @ 200 mm crs' },
};
const SHEET_L = 6.0, SHEET_W = 2.4;

const BAR_DATA = {
  6:{ label:'N6',  kgm:0.222 }, 10:{ label:'N10', kgm:0.616 },
  12:{ label:'N12', kgm:0.888 }, 16:{ label:'N16', kgm:1.578 },
  20:{ label:'N20', kgm:2.466 }, 24:{ label:'N24', kgm:3.551 },
  28:{ label:'N28', kgm:4.834 }, 32:{ label:'N32', kgm:6.313 },
  36:{ label:'N36', kgm:7.990 }, 40:{ label:'N40', kgm:9.864 },
};
const FITMENT_DATA = {
  6:{ label:'R6', kgm:0.222 }, 10:{ label:'R10', kgm:0.616 },
  12:{ label:'R12', kgm:0.888 }, 16:{ label:'R16', kgm:1.578 },
};
const STEEL_DENSITY = 7850;

const barKgm  = (d) => BAR_DATA[d]?.kgm  || FITMENT_DATA[d]?.kgm || (Math.PI/4*d*d*7850/1e6);
const barLabel = (d) => BAR_DATA[d]?.label  || `N${d}`;
const fitLabel = (d) => FITMENT_DATA[d]?.label || `R${d}`;
const fmt  = (n, dp=1) => isFinite(n) ? n.toLocaleString('en-AU',{minimumFractionDigits:dp,maximumFractionDigits:dp}) : '—';
const fmt5 = (n) => isFinite(n) ? n.toFixed(5) : '—';

/* ══════════════════════════════════════════════════════════════════
   CANVAS DRAWING UTILITIES  —  ported from app.js
══════════════════════════════════════════════════════════════════ */

const C = {
  mint50:'#f0fdf8', mint100:'#d9f7ee', mint200:'#b0eeda',
  mint300:'#79dfbf', mint400:'#45c9a0', mint500:'#22b584',
  mint600:'#159169', mint700:'#117455',
  concrete:'#d6d1c4', concreteDark:'#b8b2a4', concreteLight:'#eae7de',
  hatch:'#c4bfb0',
  steelBot:'#e05a35', steelTop:'#3a6ce8', tie:'#8b44d4',
  sideBar:'#2a8fcc', stirrup:'#8b44d4',
  starter:'#e8a020', vertBar:'#e05a35', horizBar:'#3a72d4',
  dim:'#6b7a74', dimLine:'#94a3a0',
  bg:'#f0fdf8', white:'#ffffff', text:'#374151',
  shadow:'rgba(0,0,0,0.10)',
  grid:'rgba(34,181,132,0.15)',
};

/* setupHiDPI — always renders at ≥2× physical pixels so CSS
   width:100% compresses (crisp) rather than stretches (blurry).
   During PDF export, _exportDpr=3 forces maximum resolution.   */
let _exportDpr = null;
function setupHiDPI(canvas, logW, logH) {
  const dpr = _exportDpr || Math.max(2, window.devicePixelRatio || 1);
  canvas.width  = logW * dpr;
  canvas.height = logH * dpr;
  const ctx = canvas.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

function clearCanvas(ctx, w, h) {
  ctx.clearRect(0,0,w,h);
  ctx.fillStyle = C.bg;
  ctx.fillRect(0,0,w,h);
}

function arrowTip(ctx, x, y, dx, dy, col) {
  const s = 4.5;
  ctx.save(); ctx.fillStyle = col;
  ctx.beginPath();
  ctx.moveTo(x+dx*s, y+dy*s);
  ctx.lineTo(x-dy*s*0.4, y+dx*s*0.4);
  ctx.lineTo(x+dy*s*0.4, y-dx*s*0.4);
  ctx.closePath(); ctx.fill(); ctx.restore();
}

function dimLine(ctx, x1, y1, x2, y2, label, opts={}) {
  const { offset=28, side=1, fontSize=9.5, colour=C.dim } = opts;
  ctx.save();
  ctx.strokeStyle = colour; ctx.fillStyle = colour;
  ctx.lineWidth = 0.9;
  const isH = Math.abs(x2-x1) >= Math.abs(y2-y1);
  if(isH) {
    const dy = side*offset, my = y1+dy, mx = (x1+x2)/2;
    ctx.setLineDash([2,3]);
    ctx.beginPath(); ctx.moveTo(x1,y1); ctx.lineTo(x1,my); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x2,y2); ctx.lineTo(x2,my); ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath(); ctx.moveTo(x1+3,my); ctx.lineTo(x2-3,my); ctx.stroke();
    arrowTip(ctx,x1,my,1,0,colour); arrowTip(ctx,x2,my,-1,0,colour);
    ctx.font=`${fontSize}px Inter,sans-serif`; ctx.textAlign='center'; ctx.textBaseline='middle';
    const tw = ctx.measureText(label).width+6;
    ctx.fillStyle=C.bg; ctx.fillRect(mx-tw/2,my-7,tw,13);
    ctx.fillStyle=colour; ctx.fillText(label,mx,my+(side>0?-8:8));
  } else {
    const dx = side*offset, mx = x1+dx, my = (y1+y2)/2;
    ctx.setLineDash([2,3]);
    ctx.beginPath(); ctx.moveTo(x1,y1); ctx.lineTo(mx,y1); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x2,y2); ctx.lineTo(mx,y2); ctx.stroke();
    ctx.setLineDash([]);
    ctx.beginPath(); ctx.moveTo(mx,y1+3); ctx.lineTo(mx,y2-3); ctx.stroke();
    arrowTip(ctx,mx,y1,0,1,colour); arrowTip(ctx,mx,y2,0,-1,colour);
    ctx.font=`${fontSize}px Inter,sans-serif`; ctx.textAlign='center'; ctx.textBaseline='middle';
    ctx.save(); ctx.translate(mx+(side>0?-9:9),my); ctx.rotate(-Math.PI/2);
    const tw = ctx.measureText(label).width+6;
    ctx.fillStyle=C.bg; ctx.fillRect(-tw/2,-7,tw,13);
    ctx.fillStyle=colour; ctx.fillText(label,0,0); ctx.restore();
  }
  ctx.restore();
}

function rebarDot(ctx, cx, cy, r, col) {
  ctx.beginPath(); ctx.arc(cx,cy,r,0,2*Math.PI);
  ctx.fillStyle=col; ctx.fill();
  ctx.strokeStyle='rgba(0,0,0,0.35)'; ctx.lineWidth=0.8; ctx.stroke();
  ctx.beginPath(); ctx.arc(cx-r*0.3,cy-r*0.3,r*0.28,0,2*Math.PI);
  ctx.fillStyle='rgba(255,255,255,0.4)'; ctx.fill();
}

function hatchRect(ctx, x, y, w, h, spacing=10, angle=45, col='rgba(0,0,0,0.07)') {
  ctx.save();
  ctx.strokeStyle=col; ctx.lineWidth=0.7;
  ctx.beginPath();
  const diag = Math.sqrt(w*w+h*h)*1.5;
  ctx.translate(x+w/2,y+h/2); ctx.rotate(angle*Math.PI/180);
  for(let i=-diag; i<diag; i+=spacing) { ctx.moveTo(i,-diag); ctx.lineTo(i,diag); }
  ctx.stroke(); ctx.restore();
}

function concRect(ctx, x, y, w, h, opts={}) {
  const { fill=C.concreteLight, stroke=C.concrete, lw=1.5, r=0, shadow=false } = opts;
  ctx.save();
  if(shadow) { ctx.shadowColor=C.shadow; ctx.shadowBlur=6; ctx.shadowOffsetY=2; }
  ctx.fillStyle=fill;
  if(r>0) { ctx.beginPath(); roundRect(ctx,x,y,w,h,r); ctx.fill(); }
  else { ctx.fillRect(x,y,w,h); }
  ctx.restore();
  hatchRect(ctx,x,y,w,h,12,45,'rgba(0,0,0,0.055)');
  ctx.strokeStyle=stroke; ctx.lineWidth=lw;
  if(r>0) { ctx.beginPath(); roundRect(ctx,x,y,w,h,r); ctx.stroke(); }
  else { ctx.strokeRect(x,y,w,h); }
}

function coverRect(ctx, x, y, w, h, cov) {
  ctx.save();
  ctx.strokeStyle='rgba(34,181,132,0.45)'; ctx.lineWidth=0.8; ctx.setLineDash([4,3]);
  ctx.strokeRect(x+cov,y+cov,w-2*cov,h-2*cov);
  ctx.restore();
}

function roundRect(ctx, x, y, w, h, r) {
  r = Math.min(r, w/2, h/2);
  ctx.beginPath();
  ctx.moveTo(x+r,y); ctx.arcTo(x+w,y,x+w,y+h,r);
  ctx.arcTo(x+w,y+h,x,y+h,r); ctx.arcTo(x,y+h,x,y,r);
  ctx.arcTo(x,y,x+w,y,r); ctx.closePath();
}

/* ══════════════════════════════════════════════════════════════════
   TAB 1 — FLOOR MESH  (calculations + drawing)
══════════════════════════════════════════════════════════════════ */

function calcMeshQuantities(p) {
  const { length, width, layers, lapLong, lapTrans, wastage, meshId } = p;
  const mesh = MESH_DATA[meshId] || MESH_DATA.SL82;
  if(length <= 0 || width <= 0) return { mesh, totalKg:0, totalSheets:0, m3:0, rows:[] };

  const lapLongM  = lapLong  / 1000;
  const lapTransM = lapTrans / 1000;
  const wastageD  = wastage  / 100;

  function nSheets(span, sheetSz, lap) {
    const eff = sheetSz - lap;
    if(eff <= 0.001) return Math.ceil(span / sheetSz);
    return Math.ceil((span - lap) / eff + 1e-9);
  }
  const nL = nSheets(length, SHEET_L, lapLongM);
  const nW = nSheets(width,  SHEET_W, lapTransM);
  const sheetsPerLayer = nL * nW;
  const totalSheets    = sheetsPerLayer * layers;

  const netArea       = length * width;
  const lapAreaLong   = Math.max(0, nL-1) * lapLongM * width;
  const lapAreaTrans  = Math.max(0, nW-1) * lapTransM * length;
  const lapAreaCorner = Math.max(0, nL-1) * Math.max(0, nW-1) * lapLongM * lapTransM;
  const lapArea       = lapAreaLong + lapAreaTrans - lapAreaCorner;
  const orderedArea   = netArea + lapArea;

  const kgNetPerLayer     = netArea    * mesh.kgm2;
  const kgOrderedPerLayer = orderedArea * mesh.kgm2;
  const kgAllLayers       = kgOrderedPerLayer * layers;
  const kgWastage         = kgAllLayers * wastageD;
  const totalKg           = kgAllLayers + kgWastage;
  const m3                = totalKg / STEEL_DENSITY;
  const avgKgm2           = netArea > 0 ? totalKg / netArea : 0;

  const rows = [];
  for(let ly=1; ly<=layers; ly++) {
    const lyLabel = layers > 1 ? (ly===1 ? ' — Bottom' : ' — Top') : '';
    rows.push({ layer:`Layer ${ly}${lyLabel}`, profile:mesh.id || meshId, netArea:fmt(netArea,2), sheets:sheetsPerLayer, rate:mesh.kgm2, kg:fmt(kgNetPerLayer,1) });
  }

  return { mesh, meshId, totalKg, totalSheets, m3, avgKgm2, kgAllLayers, kgWastage, kgNetPerLayer, lapArea, nL, nW, sheetsPerLayer, rows, lapLongM, lapTransM, wastageD, layers };
}

function drawMeshCanvas(canvas, p, res) {
  if(!canvas) return;
  const cw = 700, ch = 360;
  const ctx = setupHiDPI(canvas, cw, ch);
  clearCanvas(ctx, cw, ch);
  const { mesh, nL, nW, lapLongM, lapTransM } = res;
  const length = p.length, width = p.width;
  if(!length || !width || !nL) return;

  const pad = { t:44, r:60, b:50, l:56 };
  const dw = cw-pad.l-pad.r, dh = ch-pad.t-pad.b;
  const sc = Math.min(dw/length, dh/width) * 0.9;
  const pw = length*sc, ph = width*sc;
  const ox = pad.l+(dw-pw)/2, oy = pad.t+(dh-ph)/2;

  // Slab background
  ctx.save();
  ctx.shadowColor=C.shadow; ctx.shadowBlur=10; ctx.shadowOffsetY=3;
  ctx.fillStyle=C.concreteLight;
  ctx.beginPath(); roundRect(ctx,ox,oy,pw,ph,3); ctx.fill();
  ctx.restore();
  hatchRect(ctx,ox,oy,pw,ph,14,45,'rgba(0,0,0,0.045)');

  // Mesh wires
  const longSpPx  = (mesh.longSp/1000)*sc;
  const crossSpPx = (mesh.crossSp/1000)*sc;
  const longDraw  = Math.max(1.0, mesh.longD/7*1.4);
  const crossDraw = Math.max(0.7, mesh.crossD/7*1.1);
  ctx.save();
  ctx.beginPath(); ctx.rect(ox,oy,pw,ph); ctx.clip();
  ctx.strokeStyle=C.mint500; ctx.lineWidth=longDraw; ctx.globalAlpha=0.6;
  for(let yy=0; yy<=ph+crossSpPx; yy+=crossSpPx) {
    ctx.beginPath(); ctx.moveTo(ox,oy+yy); ctx.lineTo(ox+pw,oy+yy); ctx.stroke();
  }
  ctx.strokeStyle=C.mint600; ctx.lineWidth=crossDraw; ctx.globalAlpha=0.45;
  for(let xx=0; xx<=pw+longSpPx; xx+=longSpPx) {
    ctx.beginPath(); ctx.moveTo(ox+xx,oy); ctx.lineTo(ox+xx,oy+ph); ctx.stroke();
  }
  ctx.globalAlpha=1;
  ctx.restore();

  // Sheet boundaries
  const effL = SHEET_L - lapLongM;
  const effW = SHEET_W - lapTransM;
  ctx.save();
  ctx.strokeStyle=C.mint700; ctx.lineWidth=1.4; ctx.setLineDash([6,4]);

  function lapLabel(lx, ly) {
    ctx.save();
    const txt='LAP', tw=ctx.measureText(txt).width+8;
    ctx.font='bold 8px Inter,sans-serif';
    ctx.fillStyle='rgba(17,116,85,0.82)';
    ctx.beginPath(); roundRect(ctx,lx-tw/2,ly-6.5,tw,13,3); ctx.fill();
    ctx.fillStyle='#fff'; ctx.textAlign='center'; ctx.textBaseline='middle';
    ctx.fillText(txt,lx,ly); ctx.restore();
  }

  for(let i=1; i<nL; i++) {
    const sx = ox+(i*effL)*sc;
    if(sx<=ox||sx>=ox+pw) continue;
    const lapPx = lapLongM*sc;
    ctx.fillStyle='rgba(34,181,132,0.12)';
    ctx.fillRect(sx-lapPx/2,oy,lapPx,ph);
    ctx.beginPath(); ctx.moveTo(sx,oy); ctx.lineTo(sx,oy+ph); ctx.stroke();
    if(lapPx>=12) lapLabel(sx, oy+ph/2);
  }
  for(let j=1; j<nW; j++) {
    const sy = oy+(j*effW)*sc;
    if(sy<=oy||sy>=oy+ph) continue;
    const lapPx = lapTransM*sc;
    ctx.fillStyle='rgba(34,181,132,0.12)';
    ctx.fillRect(ox,sy-lapPx/2,pw,lapPx);
    ctx.beginPath(); ctx.moveTo(ox,sy); ctx.lineTo(ox+pw,sy); ctx.stroke();
    if(lapPx>=12) lapLabel(ox+pw/2, sy);
  }
  ctx.setLineDash([]); ctx.restore();

  // Sheet labels
  if(nL*nW<=20) {
    ctx.save();
    ctx.font='bold 10px Inter,sans-serif'; ctx.textAlign='center'; ctx.textBaseline='middle';
    let n=0;
    for(let i=0;i<nL;i++) for(let j=0;j<nW;j++) {
      n++;
      const cx=ox+(i*effL+SHEET_L/2)*sc, cy=oy+(j*effW+SHEET_W/2)*sc;
      if(cx>ox&&cx<ox+pw&&cy>oy&&cy<oy+ph) {
        ctx.fillStyle='rgba(17,116,85,0.55)';
        ctx.fillText(`S${n}`,cx,cy);
      }
    }
    ctx.restore();
  }

  // Slab outline
  ctx.strokeStyle=C.concrete; ctx.lineWidth=2.2;
  ctx.beginPath(); roundRect(ctx,ox,oy,pw,ph,3); ctx.stroke();

  // Profile badge
  ctx.fillStyle=C.mint600;
  ctx.beginPath(); roundRect(ctx,ox,oy-28,88,20,5); ctx.fill();
  ctx.fillStyle=C.white; ctx.font='bold 12px Inter,sans-serif';
  ctx.textAlign='center'; ctx.textBaseline='middle';
  ctx.fillText(p.meshId, ox+44, oy-18);

  // Sheet count badge
  const sheetLabel = `${nL}×${nW} = ${res.sheetsPerLayer} sheet${res.sheetsPerLayer>1?'s':''}`;
  ctx.font='10px Inter,sans-serif';
  const slw = ctx.measureText(sheetLabel).width+16;
  ctx.fillStyle='rgba(17,116,85,0.15)';
  ctx.beginPath(); roundRect(ctx,ox+pw-slw,oy-28,slw,20,5); ctx.fill();
  ctx.fillStyle=C.mint700; ctx.textAlign='center'; ctx.textBaseline='middle';
  ctx.fillText(sheetLabel, ox+pw-slw/2, oy-18);

  // Dimension lines
  dimLine(ctx,ox,oy+ph,ox+pw,oy+ph,`${fmt(length,1)} m`,{side:1,offset:30});
  dimLine(ctx,ox+pw,oy,ox+pw,oy+ph,`${fmt(width,1)} m`,{side:1,offset:38});
}

/* ══════════════════════════════════════════════════════════════════
   TAB 2 — BARS IN SLABS & FOUNDATION BEAMS
══════════════════════════════════════════════════════════════════ */

function calcBarsQuantities(p) {
  const { element, length, widthMm, depthMm, coverMm, mainDia, botCount, topCount,
          lapMm, lapsCount, tieDia, tieSp, tieEndSp, tieEndLen, slabSp, slabSpan } = p;
  const isBeam = element === 'beam';
  const mainKgm = barKgm(mainDia), tieKgm = barKgm(tieDia);
  let mainKg=0, tieKg=0, schedRows=[];

  if(isBeam) {
    const mainLen = length + lapsCount*(lapMm/1000);
    const botKg = botCount * mainLen * mainKgm;
    const topKg = topCount * mainLen * mainKgm;
    mainKg = botKg + topKg;
    const endLenM = tieEndLen/1000;
    const endZoneTies = Math.floor(endLenM/(tieEndSp/1000))+1;
    const midLen = Math.max(0, length-2*endLenM);
    const midTies = midLen>0 ? Math.ceil(midLen/(tieSp/1000))-1 : 0;
    const totalTies = 2*endZoneTies + Math.max(0,midTies);
    const bIn = widthMm - 2*coverMm - tieDia;
    const dIn = depthMm - 2*coverMm - tieDia;
    const hook = 12*tieDia/1000;
    const tieLen = 2*(bIn+dIn)/1000 + hook;
    tieKg = totalTies * tieLen * tieKgm;
    schedRows = [
      ...(botCount>0?[{bar:barLabel(mainDia), desc:`Bottom bars (${botCount} No.)`, n:botCount, lenEa:mainLen, kg:botKg}]:[]),
      ...(topCount>0?[{bar:barLabel(mainDia), desc:`Top bars (${topCount} No.)`,    n:topCount, lenEa:mainLen, kg:topKg}]:[]),
      {bar:fitLabel(tieDia), desc:`Stirrups ${tieEndSp}mm EZ / ${tieSp}mm crs (${totalTies} No.)`, n:totalTies, lenEa:tieLen, kg:tieKg},
    ];
    return { isBeam, mainKg, tieKg, total:mainKg+tieKg, m3:(mainKg+tieKg)/STEEL_DENSITY, schedRows, totalTies, tieLen };
  } else {
    const nSlabBars = Math.ceil(slabSpan/(slabSp/1000))+1;
    const barLen    = length + lapsCount*(lapMm/1000);
    mainKg = nSlabBars * barLen * mainKgm;
    schedRows = [{bar:barLabel(mainDia), desc:`Slab bars ${slabSp}mm crs (${nSlabBars} No.)`, n:nSlabBars, lenEa:barLen, kg:mainKg}];
    return { isBeam, mainKg, tieKg:0, total:mainKg, m3:mainKg/STEEL_DENSITY, schedRows, nSlabBars };
  }
}

function drawXSecCanvas(canvas, p) {
  if(!canvas) return;
  const cw = 460, ch = 360;
  const ctx = setupHiDPI(canvas, cw, ch);
  clearCanvas(ctx, cw, ch);
  const isBeam = p.element === 'beam';
  const pad = { t:36, r:24, b:40, l:44 };
  const dw = cw-pad.l-pad.r, dh = ch-pad.t-pad.b;

  if(isBeam) {
    const sc = Math.min(dw/Math.max(p.widthMm,1), dh/Math.max(p.depthMm,1))*0.85;
    const sw = p.widthMm*sc, sh = p.depthMm*sc;
    const ox = pad.l+(dw-sw)/2, oy = pad.t+(dh-sh)/2;
    concRect(ctx,ox,oy,sw,sh,{r:2,shadow:true});
    const cov = p.coverMm*sc;
    coverRect(ctx,ox,oy,sw,sh,cov);
    const barR = Math.max(3.5, Math.min(sw*0.06, p.mainDia*sc*0.5));
    const tieR = Math.max(2.5, Math.min(sw*0.04, p.tieDia*sc*0.5));
    const tx=ox+cov+tieR, ty=oy+cov+tieR, tsw=sw-2*cov-2*tieR, tsh=sh-2*cov-2*tieR;
    ctx.strokeStyle=C.stirrup; ctx.lineWidth=Math.max(2,tieR*1.5); ctx.lineJoin='round';
    ctx.strokeRect(tx,ty,tsw,tsh);
    ctx.beginPath(); ctx.moveTo(tx+tsw,ty); ctx.lineTo(tx+tsw+tieR*3,ty-tieR*3); ctx.stroke();
    if(p.botCount>0) {
      const botY=oy+sh-cov-tieR*2-barR;
      const innerW=tsw-2*barR, step=p.botCount>1?innerW/(p.botCount-1):0;
      const startX=tx+barR+(p.botCount===1?innerW/2:0);
      for(let i=0;i<p.botCount;i++) rebarDot(ctx,startX+i*step,botY,barR,C.steelBot);
    }
    if(p.topCount>0) {
      const topY=oy+cov+tieR*2+barR;
      const innerW=tsw-2*barR, step=p.topCount>1?innerW/(p.topCount-1):0;
      const startX=tx+barR+(p.topCount===1?innerW/2:0);
      for(let i=0;i<p.topCount;i++) rebarDot(ctx,startX+i*step,topY,barR,C.steelTop);
    }
    dimLine(ctx,ox,oy+sh,ox+sw,oy+sh,`${p.widthMm} mm`,{side:1,offset:22,fontSize:9});
    dimLine(ctx,ox,oy,ox,oy+sh,`${p.depthMm} mm`,{side:-1,offset:26,fontSize:9});
    ctx.font='9px Inter,sans-serif'; ctx.textBaseline='middle'; ctx.textAlign='left';
    let ly=oy+6; const lx=ox+sw+6;
    if(p.botCount>0){ rebarDot(ctx,lx+4,ly+4,4,C.steelBot); ctx.fillStyle=C.steelBot; ctx.fillText(`${p.botCount}×${barLabel(p.mainDia)}`,lx+11,ly+4); ly+=15; }
    if(p.topCount>0){ rebarDot(ctx,lx+4,ly+4,4,C.steelTop); ctx.fillStyle=C.steelTop; ctx.fillText(`${p.topCount}×${barLabel(p.mainDia)}`,lx+11,ly+4); ly+=15; }
    rebarDot(ctx,lx+4,ly+4,3,C.stirrup); ctx.fillStyle=C.stirrup; ctx.fillText(fitLabel(p.tieDia),lx+11,ly+4);
  } else {
    const slabDepth = p.depthMm || 200;
    const displaySpanMm = Math.max(p.slabSpan*1000, p.slabSp*8);
    const sc = Math.min(dw/displaySpanMm, dh/slabDepth)*0.82;
    const sw = displaySpanMm*sc, sh = slabDepth*sc;
    const ox = pad.l+(dw-sw)/2, oy = pad.t+(dh-sh)/2;
    concRect(ctx,ox,oy,sw,sh,{r:2,shadow:true});
    const cov = Math.max(p.coverMm,20)*sc;
    coverRect(ctx,ox,oy,sw,sh,cov);
    const stepPx = p.slabSp*sc;
    const barR = Math.max(2.5, Math.min(stepPx*0.35, slabDepth*sc*0.12));
    const botY = oy+sh-cov-barR;
    let bx=ox+cov+barR, idx=0;
    while(bx<=ox+sw-cov-barR && idx<60) { rebarDot(ctx,bx,botY,barR,C.steelBot); bx+=stepPx; idx++; }
    if(stepPx>28) dimLine(ctx,ox+cov+barR,oy+sh,ox+cov+barR+stepPx,oy+sh,`${p.slabSp} mm crs`,{side:1,offset:22,fontSize:9});
    dimLine(ctx,ox,oy,ox,oy+sh,`${slabDepth} mm`,{side:-1,offset:26,fontSize:9});
    ctx.font='bold 9px Inter,sans-serif'; ctx.fillStyle=C.mint600;
    ctx.textAlign='center'; ctx.textBaseline='top';
    ctx.fillText(`${barLabel(p.mainDia)} @ ${p.slabSp} mm crs`, ox+sw/2, oy-18);
  }
}

function drawLongCanvas(canvas, p) {
  if(!canvas || !p.length) return;
  const cw = 700, ch = 360;
  const ctx = setupHiDPI(canvas, cw, ch);
  clearCanvas(ctx, cw, ch);
  const isBeam = p.element === 'beam';
  const pad = { t:52, r:30, b:54, l:54 };
  const dw = cw-pad.l-pad.r, dh = ch-pad.t-pad.b;
  const scX = dw / Math.max(p.length,0.001);
  const scY = isBeam ? Math.min(dh/Math.max(p.depthMm,1)*1000, dh/4) : Math.min(dh/Math.max(p.depthMm||200,1)*1000, dh/3);
  const bw = p.length*scX, bh = isBeam ? p.depthMm*scY/1000 : (p.depthMm||200)*scY/1000;
  const ox = pad.l+(dw-bw)/2, oy = pad.t+(dh-bh)/2;
  const cov = Math.max(p.coverMm,20)*scY/1000;
  concRect(ctx,ox,oy,bw,bh,{r:2,shadow:true});
  coverRect(ctx,ox,oy,bw,bh,cov);
  const mainR = Math.max(3, Math.min(p.mainDia*scY/1000*0.45, bh*0.09));
  const tieW  = Math.max(2, Math.min(p.tieDia*scY/1000*0.40, bh*0.07));

  if(isBeam) {
    const endLenPx = Math.min((p.tieEndLen/1000)*scX, bw*0.35);
    const midStart = ox+endLenPx, midEnd = ox+bw-endLenPx;
    ctx.fillStyle='rgba(139,68,212,0.06)';
    ctx.fillRect(ox,oy,endLenPx,bh); ctx.fillRect(ox+bw-endLenPx,oy,endLenPx,bh);
    if(endLenPx>55) {
      ctx.font='bold 11px Inter,sans-serif'; ctx.textAlign='center'; ctx.textBaseline='middle';
      ctx.fillStyle='rgba(139,68,212,0.55)';
      ctx.fillText('EZ',ox+endLenPx/2,oy+bh/2); ctx.fillText('EZ',ox+bw-endLenPx/2,oy+bh/2);
    }
    if(midEnd-midStart>70) {
      ctx.font='11px Inter,sans-serif'; ctx.fillStyle='rgba(100,100,100,0.30)';
      ctx.fillText('MZ',(midStart+midEnd)/2,oy+bh/2);
    }
    const endSpPx=Math.max(4,(p.tieEndSp/1000)*scX), midSpPx=Math.max(4,(p.tieSp/1000)*scX);
    const drawTies=(x0,x1,sp)=>{ let x=x0; while(x<=x1+0.5){ ctx.strokeStyle=C.stirrup; ctx.lineWidth=tieW; ctx.lineCap='square'; ctx.beginPath(); ctx.moveTo(x,oy+cov); ctx.lineTo(x,oy+bh-cov); ctx.stroke(); x+=Math.max(sp,1); }};
    drawTies(ox,ox+endLenPx,endSpPx); drawTies(ox+bw-endLenPx,ox+bw,endSpPx);
    if(midEnd>midStart+midSpPx) drawTies(midStart+midSpPx,midEnd,midSpPx);
    if(endLenPx>40) dimLine(ctx,ox,oy,ox+endLenPx,oy,`EZ — ${p.tieEndLen}mm @${p.tieEndSp}mm`,{side:-1,offset:26,colour:'rgba(130,60,200,0.75)',fontSize:9.5});
    if(midEnd-midStart>80) dimLine(ctx,midStart,oy,midEnd,oy,`@ ${p.tieSp}mm crs`,{side:-1,offset:26,colour:'rgba(100,100,100,0.65)',fontSize:9.5});
    const botY=oy+bh-cov-mainR;
    ctx.lineCap='round';
    const nBot=Math.min(p.botCount,3);
    for(let i=0;i<nBot;i++) { const off=(i-(nBot-1)/2)*mainR*2.6; ctx.strokeStyle=C.steelBot; ctx.lineWidth=mainR*2; ctx.beginPath(); ctx.moveTo(ox+3,botY+off); ctx.lineTo(ox+bw-3,botY+off); ctx.stroke(); }
    if(p.topCount>0) {
      const topY=oy+cov+mainR, nTop=Math.min(p.topCount,2);
      for(let i=0;i<nTop;i++) { const off=(i-(nTop-1)/2)*mainR*2.6; ctx.strokeStyle=C.steelTop; ctx.lineWidth=mainR*2; ctx.beginPath(); ctx.moveTo(ox+3,topY+off); ctx.lineTo(ox+bw-3,topY+off); ctx.stroke(); }
    }
    if(p.lapsCount>0&&p.lapMm>0) {
      const lapPx=(p.lapMm/1000)*scX, lapX=ox+bw*0.55-lapPx/2;
      ctx.fillStyle='rgba(232,91,58,0.11)'; ctx.fillRect(lapX,oy+bh*0.52,lapPx,bh*0.40);
      ctx.strokeStyle='rgba(232,91,58,0.55)'; ctx.lineWidth=1; ctx.setLineDash([4,3]);
      ctx.beginPath(); ctx.moveTo(lapX,oy+bh*0.42); ctx.lineTo(lapX,oy+bh); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(lapX+lapPx,oy+bh*0.42); ctx.lineTo(lapX+lapPx,oy+bh); ctx.stroke();
      ctx.setLineDash([]);
      if(lapPx>38){ ctx.fillStyle='rgba(200,70,30,0.85)'; ctx.font='bold 10px Inter,sans-serif'; ctx.textAlign='center'; ctx.textBaseline='middle'; ctx.fillText(`Lap ${p.lapMm}mm`,lapX+lapPx/2,oy+bh*0.72); }
    }
    dimLine(ctx,ox,oy,ox,oy+bh,`${p.depthMm} mm`,{side:-1,offset:34,fontSize:10});
    const lx=ox+bw+10; let legY=oy;
    [{col:C.steelBot,lbl:`${p.botCount}×${barLabel(p.mainDia)} Bot`},
     ...(p.topCount>0?[{col:C.steelTop,lbl:`${p.topCount}×${barLabel(p.mainDia)} Top`}]:[]),
     {col:C.stirrup,lbl:`${fitLabel(p.tieDia)}@${p.tieSp}mm`},
    ].forEach(({col,lbl})=>{ ctx.fillStyle=col; ctx.font='bold 9.5px Inter,sans-serif'; ctx.textBaseline='top'; ctx.textAlign='left'; ctx.fillText('▬ '+lbl,lx,legY); legY+=16; });
  } else {
    const spPx=Math.max(5,(p.slabSp/1000)*scX);
    const botY=oy+bh-cov-mainR;
    ctx.lineCap='butt';
    let bx=ox+cov;
    while(bx<=ox+bw-cov){ ctx.strokeStyle=C.steelBot; ctx.lineWidth=mainR*2; ctx.beginPath(); ctx.moveTo(bx,botY); ctx.lineTo(bx+Math.min(p.length*scX,bw),botY); ctx.stroke(); bx+=spPx; }
    if(spPx>40&&spPx<bw*0.45) dimLine(ctx,ox+cov,oy+bh,ox+cov+spPx,oy+bh,`${p.slabSp}mm`,{side:1,offset:26,fontSize:9.5});
    dimLine(ctx,ox,oy,ox,oy+bh,`${p.depthMm||200}mm`,{side:-1,offset:34,fontSize:10});
    ctx.fillStyle=C.steelBot; ctx.font='bold 10.5px Inter,sans-serif'; ctx.textAlign='left'; ctx.textBaseline='top';
    ctx.fillText(`${barLabel(p.mainDia)} @ ${p.slabSp}mm crs`,ox,oy-26);
  }
  dimLine(ctx,ox,oy+bh,ox+bw,oy+bh,`${fmt(p.length,1)} m`,{side:1,offset:34,fontSize:10.5});
}

/* ══════════════════════════════════════════════════════════════════
   TAB 3 — CONCRETE WALLS
══════════════════════════════════════════════════════════════════ */

function calcWallsQuantities(p) {
  const nVert   = Math.ceil(p.length/(p.vertSp/1000))+1;
  const vertLen = p.height + p.vertLapCount*(p.vertLapMm/1000);
  const vertKg  = nVert * vertLen * barKgm(p.vertDia) * p.vertFaces;
  const nHoriz  = Math.ceil(p.height/(p.horizSp/1000))+1;
  const horizLen= p.length + p.horizLapMm/1000;
  const horizKg = nHoriz * horizLen * barKgm(p.horizDia) * p.vertFaces;
  const nStart  = Math.ceil(p.length/(p.starterSp/1000))+1;
  const startLen= p.starterLenMm/1000;
  const startKg = nStart * startLen * barKgm(p.starterDia) * p.starterFaces;
  const total   = vertKg+horizKg+startKg;
  const m3      = total/STEEL_DENSITY;
  const wallArea= p.length*p.height;
  const kgm2    = wallArea>0 ? total/wallArea : 0;
  return { total, vertKg, horizKg, startKg, m3, kgm2, nVert, nHoriz, nStart, vertLen, horizLen, startLen, wallArea };
}

function drawWallCanvas(canvas, p, res) {
  if(!canvas||!p.length||!p.height) return;
  const cw = 700, ch = 420;
  const ctx = setupHiDPI(canvas, cw, ch);
  clearCanvas(ctx, cw, ch);
  const { nVert, nHoriz, nStart } = res;
  const isBlock = p.type==='block';
  const starterShowH = Math.min(p.starterLenMm/1000, 0.6);
  const totalHm = p.height + starterShowH;
  const pad = { t:56, r:76, b:52, l:72 };
  const dw=cw-pad.l-pad.r, dh=ch-pad.t-pad.b;
  const sc=Math.min(dw/(p.length||1), dh/(totalHm||1))*0.92;
  const wallW=p.length*sc, wallH=p.height*sc, footH=starterShowH*sc;
  const ox=pad.l+(dw-wallW)/2, oy=pad.t+(dh-(wallH+footH))/2, footY=oy+wallH;

  // Footing
  ctx.save(); ctx.shadowColor=C.shadow; ctx.shadowBlur=6; ctx.shadowOffsetY=2;
  ctx.fillStyle='#d0cbbe'; ctx.fillRect(ox-30,footY,wallW+60,footH+22); ctx.restore();
  ctx.strokeStyle='#a89f90'; ctx.lineWidth=1.5; ctx.strokeRect(ox-30,footY,wallW+60,footH+22);
  hatchRect(ctx,ox-30,footY,wallW+60,footH+22,14,45,'rgba(0,0,0,0.05)');
  ctx.fillStyle='rgba(80,60,30,0.35)'; ctx.font='bold 9px Inter,sans-serif';
  ctx.textAlign='left'; ctx.textBaseline='top';
  ctx.fillText('FOOTING / FOUNDATION',ox-28,footY+4);

  // Wall body
  if(isBlock) {
    const bkW=Math.max(20,(390/p.length)*wallW/10), bkH=Math.max(12,(190/p.height)*wallH/10);
    ctx.fillStyle='#e6e0d3'; ctx.fillRect(ox,oy,wallW,wallH);
    for(let row=0; row*bkH<wallH+bkH; row++) {
      const by=footY-row*bkH-bkH, offset=(row%2===0?0:bkW/2);
      for(let col=-1; col*bkW<wallW+bkW; col++) {
        const bx=ox+col*bkW-offset;
        const clampX=Math.max(ox,bx), clampW=Math.min(ox+wallW,bx+bkW)-clampX;
        const clampY=Math.max(oy,by), clampH=Math.min(oy+wallH,by+bkH)-clampY;
        if(clampW>0&&clampH>0) {
          ctx.fillStyle=row%2===0?'#e8e3d8':'#ddd8cc'; ctx.fillRect(clampX,clampY,clampW,clampH);
          ctx.strokeStyle='#b8b2a4'; ctx.lineWidth=0.7; ctx.strokeRect(clampX,clampY,clampW,clampH);
        }
      }
    }
  } else { concRect(ctx,ox,oy,wallW,wallH,{shadow:true}); }
  ctx.strokeStyle=C.concreteDark; ctx.lineWidth=2; ctx.strokeRect(ox,oy,wallW,wallH);

  // Cover guides
  ctx.save(); ctx.strokeStyle='rgba(34,181,132,0.35)'; ctx.lineWidth=0.8; ctx.setLineDash([4,4]);
  ctx.beginPath(); ctx.moveTo(ox+10,oy+4); ctx.lineTo(ox+10,oy+wallH-4); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(ox+wallW-10,oy+4); ctx.lineTo(ox+wallW-10,oy+wallH-4); ctx.stroke();
  ctx.setLineDash([]); ctx.restore();

  // Vertical bars
  const vertSpPx=(p.vertSp/1000)*sc, vertR=Math.max(1.5,Math.min(4,p.vertDia*0.13));
  const maxShow=Math.min(nVert,80);
  ctx.strokeStyle=C.vertBar;
  for(let i=0;i<maxShow;i++) {
    const bx=ox+Math.min(i*(p.vertSp/1000)*sc,wallW);
    if(bx>ox+wallW+2) break;
    ctx.lineWidth=vertR*1.6; ctx.lineCap='round';
    ctx.beginPath(); ctx.moveTo(bx,oy+4); ctx.lineTo(bx,footY+footH*0.65); ctx.stroke();
    const bendLen=Math.min((p.thickMm/1000)*sc*1.2,18);
    ctx.beginPath(); ctx.moveTo(bx,footY+footH*0.65); ctx.lineTo(bx+bendLen,footY+footH*0.65); ctx.stroke();
  }

  // Horizontal bars
  const horizR=Math.max(1.2,Math.min(3.5,p.horizDia*0.12));
  ctx.strokeStyle=C.horizBar; ctx.lineWidth=horizR*1.5; ctx.setLineDash([6,4]);
  for(let j=0;j<Math.min(nHoriz,50);j++) {
    const by=footY-(j+0.5)*(p.horizSp/1000)*sc;
    if(by<oy-2||by>footY+2) continue;
    ctx.beginPath(); ctx.moveTo(ox+4,by); ctx.lineTo(ox+wallW-4,by); ctx.stroke();
  }
  ctx.setLineDash([]);

  // Starter bars
  const startR=Math.max(1.5,Math.min(4.5,p.starterDia*0.14));
  ctx.strokeStyle=C.starter; ctx.lineWidth=startR*1.7; ctx.lineCap='round';
  for(let k=0;k<Math.min(nStart,80);k++) {
    const bx=ox+Math.min(k*(p.starterSp/1000)*sc,wallW);
    if(bx>ox+wallW+2) break;
    ctx.globalAlpha=0.88;
    ctx.beginPath(); ctx.moveTo(bx+1.5,oy+wallH-8); ctx.lineTo(bx+1.5,footY+footH*0.58); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(bx+1.5,footY+footH*0.58); ctx.lineTo(bx+1.5+Math.min(12,(p.thickMm/1000)*sc),footY+footH*0.58); ctx.stroke();
    ctx.globalAlpha=1;
  }

  // Callout dimensions
  const horizSpPx=(p.horizSp/1000)*sc;
  if(horizSpPx>25&&nHoriz>1) {
    const by0=footY-0.5*(p.horizSp/1000)*sc, by1=footY-1.5*(p.horizSp/1000)*sc;
    if(by0>=oy&&by1>=oy) dimLine(ctx,ox-12,by0,ox-12,by1,`${p.horizSp}mm`,{side:-1,offset:24,colour:C.horizBar,fontSize:8});
  }
  if(vertSpPx>18&&maxShow>1) dimLine(ctx,ox,oy+wallH+8,ox+vertSpPx,oy+wallH+8,`${p.vertSp}mm`,{side:1,offset:22,colour:C.vertBar,fontSize:8});
  const strPx=footH*0.65;
  if(strPx>20) dimLine(ctx,ox+wallW+10,footY,ox+wallW+10,footY+strPx,`${p.starterLenMm}mm str.`,{side:1,offset:28,colour:C.starter,fontSize:8});
  dimLine(ctx,ox,oy+wallH,ox+wallW,oy+wallH,`${fmt(p.length,1)} m`,{side:1,offset:40});
  dimLine(ctx,ox+wallW,oy,ox+wallW,oy+wallH,`${fmt(p.height,1)} m`,{side:1,offset:46});

  // Title
  ctx.fillStyle=C.mint700; ctx.font='bold 10px Inter,sans-serif'; ctx.textAlign='left'; ctx.textBaseline='top';
  ctx.fillText(`${isBlock?'Blockwork':'In-Situ Concrete'} Wall — ${p.thickMm}mm thk`,ox,oy-42);
  ctx.fillStyle=C.text; ctx.font='9.5px Inter,sans-serif';
  ctx.fillText(`Vert: ${barLabel(p.vertDia)}@${p.vertSp}crs · Horiz: ${barLabel(p.horizDia)}@${p.horizSp}crs · Starter: ${barLabel(p.starterDia)}@${p.starterSp}crs`,ox,oy-28);
}

/* ══════════════════════════════════════════════════════════════════
   TAB 4 — COLUMN / PIER CAGES
══════════════════════════════════════════════════════════════════ */

function columnBarPositionsRect(n, ox, oy, w, h) {
  const corners = [[ox,oy],[ox+w,oy],[ox+w,oy+h],[ox,oy+h]];
  if(n<=4) return corners.slice(0,n);
  const perimeter = 2*(w+h), step = perimeter/n;
  const positions = [];
  for(let i=0;i<n;i++) {
    let d=i*step, px, py;
    if(d<w){ px=ox+d; py=oy; }
    else if(d<w+h){ d-=w; px=ox+w; py=oy+d; }
    else if(d<2*w+h){ d-=(w+h); px=ox+w-d; py=oy+h; }
    else { d-=(2*w+h); px=ox; py=oy+h-d; }
    positions.push([px,py]);
  }
  return positions;
}

function calcColsQuantities(p) {
  const isRect = p.shape==='rect';
  const mainKgm = barKgm(p.mainDia), ligKgm = barKgm(p.ligDia);
  const mainLen = p.length + p.lapsCount*(p.lapMm/1000);
  const mainKg  = p.nBars * mainLen * mainKgm;
  let ligPerim = 0;
  if(isRect) {
    const bIn=p.bMm-2*p.coverMm-p.ligDia, dIn=p.dMm-2*p.coverMm-p.ligDia;
    ligPerim=2*(bIn+dIn)/1000+10*p.ligDia/1000;
  } else {
    const rIn=(p.diamMm/2-p.coverMm-p.ligDia/2)/1000;
    ligPerim=2*Math.PI*rIn+10*p.ligDia/1000;
  }
  const endLenM=p.ligEndLen/1000, endTies=Math.floor(endLenM/(p.ligEndSp/1000))+1;
  const midLen=Math.max(0,p.length-2*endLenM);
  const midTies=midLen>0?Math.ceil(midLen/(p.ligSp/1000))-1:0;
  const totalLigs=2*endTies+Math.max(0,midTies);
  const ligKg=totalLigs*ligPerim*ligKgm;
  const total=mainKg+ligKg, m3=total/STEEL_DENSITY;
  return { total, mainKg, ligKg, m3, totalLigs, endTies, midTies, ligPerim, mainLen };
}

function drawColXSecCanvas(canvas, p) {
  if(!canvas) return;
  const cw = 460, ch = 380;
  const ctx = setupHiDPI(canvas, cw, ch);
  clearCanvas(ctx, cw, ch);
  const isRect=p.shape==='rect';
  const pad={t:32,r:24,b:36,l:36};
  const dw=cw-pad.l-pad.r, dh=ch-pad.t-pad.b;

  if(isRect) {
    const sc=Math.min(dw/Math.max(p.bMm,1),dh/Math.max(p.dMm,1))*0.86;
    const sw=p.bMm*sc, sh=p.dMm*sc;
    const cx=pad.l+(dw-sw)/2, cy=pad.t+(dh-sh)/2;
    concRect(ctx,cx,cy,sw,sh,{r:3,shadow:true});
    coverRect(ctx,cx,cy,sw,sh,p.coverMm*sc);
    const cov=p.coverMm*sc, lR=Math.max(1.8,p.ligDia*sc/p.bMm*8);
    ctx.strokeStyle=C.stirrup; ctx.lineWidth=lR*1.2; ctx.lineJoin='round';
    ctx.strokeRect(cx+cov+lR/2,cy+cov+lR/2,sw-2*cov-lR,sh-2*cov-lR);
    const barR=Math.max(3,p.mainDia*sc/p.bMm*12);
    const bars=columnBarPositionsRect(p.nBars,
      cx+cov+p.ligDia*sc/p.bMm*8+barR, cy+cov+p.ligDia*sc/p.dMm*8+barR,
      (p.bMm-2*p.coverMm-p.ligDia-p.mainDia)*sc, (p.dMm-2*p.coverMm-p.ligDia-p.mainDia)*sc);
    bars.forEach(([bx,by])=>rebarDot(ctx,bx,by,barR,C.steelBot));
    dimLine(ctx,cx,cy+sh,cx+sw,cy+sh,`${p.bMm} mm`,{side:1,offset:22,fontSize:8.5});
    dimLine(ctx,cx,cy,cx,cy+sh,`${p.dMm} mm`,{side:-1,offset:22,fontSize:8.5});
    ctx.font='8.5px Inter,sans-serif'; ctx.textBaseline='top'; ctx.textAlign='left';
    rebarDot(ctx,pad.l+4,4+4,4,C.steelBot); ctx.fillStyle=C.steelBot; ctx.fillText(`${p.nBars}×${barLabel(p.mainDia)}`,pad.l+12,4);
    rebarDot(ctx,pad.l+4,4+16,3,C.stirrup); ctx.fillStyle=C.stirrup; ctx.fillText(`${fitLabel(p.ligDia)} lig.`,pad.l+12,16);
  } else {
    const sc=Math.min(dw,dh)/Math.max(p.diamMm,1)*0.86;
    const r=p.diamMm*sc/2, cx=pad.l+dw/2, cy=pad.t+dh/2;
    ctx.save(); ctx.shadowColor=C.shadow; ctx.shadowBlur=8;
    ctx.beginPath(); ctx.arc(cx,cy,r,0,2*Math.PI); ctx.fillStyle=C.concreteLight; ctx.fill(); ctx.restore();
    hatchRect(ctx,cx-r,cy-r,r*2,r*2,12,45,'rgba(0,0,0,0.05)');
    ctx.beginPath(); ctx.arc(cx,cy,r,0,2*Math.PI); ctx.strokeStyle=C.concrete; ctx.lineWidth=2; ctx.stroke();
    const covR=(p.diamMm/2-p.coverMm)*sc;
    ctx.beginPath(); ctx.arc(cx,cy,covR,0,2*Math.PI);
    ctx.strokeStyle='rgba(34,181,132,0.4)'; ctx.lineWidth=0.8; ctx.setLineDash([4,3]); ctx.stroke(); ctx.setLineDash([]);
    const ligR=(p.diamMm/2-p.coverMm-p.ligDia/2)*sc;
    ctx.beginPath(); ctx.arc(cx,cy,ligR,0,2*Math.PI);
    ctx.strokeStyle=C.stirrup; ctx.lineWidth=Math.max(2,p.ligDia*sc/p.diamMm*10); ctx.stroke();
    const barR=Math.max(3,p.mainDia*sc/p.diamMm*12);
    const barRadius=(p.diamMm/2-p.coverMm-p.ligDia-p.mainDia/2)*sc;
    for(let i=0;i<p.nBars;i++) {
      const angle=(2*Math.PI*i/p.nBars)-Math.PI/2;
      rebarDot(ctx,cx+barRadius*Math.cos(angle),cy+barRadius*Math.sin(angle),barR,C.steelBot);
    }
    dimLine(ctx,cx-r,cy+r,cx+r,cy+r,`⌀${p.diamMm} mm`,{side:1,offset:22,fontSize:8.5});
    ctx.font='8.5px Inter,sans-serif'; ctx.textBaseline='top'; ctx.textAlign='left';
    rebarDot(ctx,pad.l+4,4+4,4,C.steelBot); ctx.fillStyle=C.steelBot; ctx.fillText(`${p.nBars}×${barLabel(p.mainDia)}`,pad.l+12,4);
    rebarDot(ctx,pad.l+4,4+16,3,C.stirrup); ctx.fillStyle=C.stirrup; ctx.fillText(`${fitLabel(p.ligDia)} spiral`,pad.l+12,16);
  }
}

function drawColElevCanvas(canvas, p, res) {
  if(!canvas||!p.length) return;
  const cw = 460, ch = 480;
  const ctx = setupHiDPI(canvas, cw, ch);
  clearCanvas(ctx, cw, ch);
  const isRect=p.shape==='rect';
  const sectionDim=isRect?p.bMm:p.diamMm;
  const pad={t:36,r:36,b:42,l:40};
  const dw=cw-pad.l-pad.r, dh=ch-pad.t-pad.b;
  const scX=Math.min(dw/Math.max(sectionDim,1)*1000,dh/4);
  const scY=dh/p.length;
  const bw=Math.min(sectionDim*scX/1000,dw*0.42), bh=Math.min(p.length*scY,dh);
  const ox=pad.l+(dw-bw)/2, oy=pad.t+(dh-bh)/2;
  concRect(ctx,ox,oy,bw,bh,{shadow:true});
  const cov=p.coverMm/sectionDim*bw;
  coverRect(ctx,ox,oy,bw,bh,cov);
  const mainR=Math.max(2,p.mainDia/sectionDim*bw*0.5);
  const ligW=Math.max(1.5,p.ligDia/sectionDim*bw*0.45);
  const endLenPx=(p.ligEndLen/1000)*scY, endSpPx=(p.ligEndSp/1000)*scY, ligSpPx=(p.ligSp/1000)*scY;
  ctx.fillStyle='rgba(139,68,212,0.07)';
  ctx.fillRect(ox,oy,bw,endLenPx); ctx.fillRect(ox,oy+bh-endLenPx,bw,endLenPx);
  ctx.fillStyle='rgba(139,68,212,0.7)'; ctx.font='8px Inter,sans-serif'; ctx.textAlign='center'; ctx.textBaseline='middle';
  if(endLenPx>20){ ctx.fillText(`EZ@${p.ligEndSp}`,ox+bw/2,oy+endLenPx/2); ctx.fillText(`EZ@${p.ligEndSp}`,ox+bw/2,oy+bh-endLenPx/2); }
  if(bh-2*endLenPx>20) ctx.fillText(`@${p.ligSp}crs`,ox+bw/2,oy+bh/2);
  const drawLigs=(yStart,yEnd,sp)=>{ let y=yStart; while(y<=yEnd+0.5){ ctx.strokeStyle=C.stirrup; ctx.lineWidth=ligW; ctx.beginPath(); ctx.moveTo(ox+cov,y); ctx.lineTo(ox+bw-cov,y); ctx.stroke(); y+=Math.max(sp,1); }};
  drawLigs(oy,oy+endLenPx,endSpPx); drawLigs(oy+bh-endLenPx,oy+bh,endSpPx);
  if(bh-2*endLenPx>ligSpPx) drawLigs(oy+endLenPx+ligSpPx,oy+bh-endLenPx-ligSpPx/2,ligSpPx);
  [ox+cov+mainR, ox+bw-cov-mainR].forEach(bx=>{ ctx.lineWidth=mainR*1.8; ctx.lineCap='round'; ctx.strokeStyle=C.steelBot; ctx.beginPath(); ctx.moveTo(bx,oy+2); ctx.lineTo(bx,oy+bh-2); ctx.stroke(); });
  if(p.lapsCount>0&&p.lapMm>0) {
    const lapPx=(p.lapMm/1000)*scY, lapY=oy+bh/2-lapPx/2;
    ctx.fillStyle='rgba(232,91,58,0.12)'; ctx.fillRect(ox,lapY,bw,lapPx);
    if(lapPx>20){ ctx.fillStyle=C.steelBot; ctx.font='8px Inter,sans-serif'; ctx.textAlign='center'; ctx.textBaseline='middle'; ctx.fillText(`Lap ${p.lapMm}mm`,ox+bw/2,lapY+lapPx/2); }
  }
  dimLine(ctx,ox,oy,ox,oy+bh,`${fmt(p.length,1)} m`,{side:-1,offset:28});
  dimLine(ctx,ox,oy,ox+bw,oy,`${sectionDim} mm`,{side:-1,offset:20,fontSize:8.5});
  ctx.fillStyle=C.text; ctx.font='bold 9px Inter,sans-serif'; ctx.textAlign='left'; ctx.textBaseline='top';
  ctx.fillText(`${isRect?`${p.bMm}×${p.dMm}mm`:`⌀${p.diamMm}mm`} | ${p.nBars}×${barLabel(p.mainDia)} | ${fitLabel(p.ligDia)}@${p.ligSp}crs`, ox, 4);
}

/* ══════════════════════════════════════════════════════════════════
   PAN / ZOOM CANVAS WRAPPER
══════════════════════════════════════════════════════════════════ */

function PanZoomCanvas({ children }) {
  const containerRef = useRef(null);
  const [tf, setTf]  = useState({ s: 1, x: 0, y: 0 });
  const dragging     = useRef(false);
  const last         = useRef({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  /* ── Wheel zoom towards cursor ── */
  const onWheel = useCallback((e) => {
    e.preventDefault();
    const rect   = containerRef.current.getBoundingClientRect();
    const mx     = e.clientX - rect.left - rect.width  / 2;
    const my     = e.clientY - rect.top  - rect.height / 2;
    const factor = e.deltaY < 0 ? 1.14 : 1 / 1.14;
    setTf(t => {
      const newS = Math.min(8, Math.max(0.25, t.s * factor));
      const ratio = newS / t.s;
      return { s: newS, x: mx - (mx - t.x) * ratio, y: my - (my - t.y) * ratio };
    });
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  /* ── Mouse drag ── */
  const onMouseDown = (e) => {
    dragging.current = true;
    setIsDragging(true);
    last.current = { x: e.clientX, y: e.clientY };
  };
  const onMouseMove = (e) => {
    if (!dragging.current) return;
    const dx = e.clientX - last.current.x;
    const dy = e.clientY - last.current.y;
    last.current = { x: e.clientX, y: e.clientY };
    setTf(t => ({ ...t, x: t.x + dx, y: t.y + dy }));
  };
  const onMouseUp = () => { dragging.current = false; setIsDragging(false); };

  /* ── Touch drag & pinch-zoom ── */
  const lastTouch = useRef(null);
  const onTouchStart = (e) => {
    if (e.touches.length === 1) {
      dragging.current = true;
      last.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    lastTouch.current = e.touches;
  };
  const onTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 2 && lastTouch.current?.length === 2) {
      const dist = (ts) => Math.hypot(ts[0].clientX-ts[1].clientX, ts[0].clientY-ts[1].clientY);
      const prevD = dist(lastTouch.current), curD = dist(e.touches);
      const factor = curD / prevD;
      const rect = containerRef.current.getBoundingClientRect();
      const mx = (e.touches[0].clientX + e.touches[1].clientX)/2 - rect.left - rect.width/2;
      const my = (e.touches[0].clientY + e.touches[1].clientY)/2 - rect.top  - rect.height/2;
      setTf(t => {
        const newS = Math.min(8, Math.max(0.25, t.s * factor));
        const ratio = newS / t.s;
        return { s: newS, x: mx - (mx - t.x) * ratio, y: my - (my - t.y) * ratio };
      });
    } else if (e.touches.length === 1 && dragging.current) {
      const dx = e.touches[0].clientX - last.current.x;
      const dy = e.touches[0].clientY - last.current.y;
      last.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      setTf(t => ({ ...t, x: t.x + dx, y: t.y + dy }));
    }
    lastTouch.current = e.touches;
  };
  const onTouchEnd = () => { dragging.current = false; lastTouch.current = null; };

  const reset = () => setTf({ s: 1, x: 0, y: 0 });
  const zoom  = (dir) => setTf(t => ({ ...t, s: Math.min(8, Math.max(0.25, t.s * (dir > 0 ? 1.25 : 0.8))) }));

  return (
    <div
      ref={containerRef}
      className="rc-panzoom-wrap"
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="rc-panzoom-inner"
        style={{ transform: `translate(${tf.x}px,${tf.y}px) scale(${tf.s})`, transformOrigin: 'center center' }}
      >
        {children}
      </div>

      {/* Controls */}
      <div className="rc-panzoom-controls" onMouseDown={e=>e.stopPropagation()}>
        <button type="button" className="rc-pz-btn" onClick={()=>zoom(1)} title="Zoom in">+</button>
        <button type="button" className="rc-pz-btn" onClick={()=>zoom(-1)} title="Zoom out">−</button>
        <button type="button" className="rc-pz-btn rc-pz-btn--reset" onClick={reset} title="Reset view">↺</button>
      </div>

      {/* Hint */}
      <div className="rc-panzoom-hint">🔍 Drag to pan · Scroll to zoom</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   EXPORT MODAL
══════════════════════════════════════════════════════════════════ */

const TAB_EXPORT_DEFS = [
  { id:'mesh',  label:'Floor Mesh' },
  { id:'bars',  label:'Bars & Beams' },
  { id:'walls', label:'Concrete Walls' },
  { id:'cols',  label:'Columns & Piers' },
];

function ExportModal({ onClose, canvases, results, params }) {
  const [selected, setSelected] = useState({ mesh:true, bars:true, walls:true, cols:true });
  const [exporting, setExporting] = useState(false);

  const toggleAll = (val) => setSelected({ mesh:val, bars:val, walls:val, cols:val });
  const allOn  = Object.values(selected).every(Boolean);
  const noneOn = Object.values(selected).every(v=>!v);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation:'portrait', unit:'mm', format:'a4' });
      const pw = 210, ph = 297, mg = 14;
      let y = mg;

      /* ── Load logo + detect true aspect ratio ── */
      let logoData = null;
      let logoW = 0, logoH = 0;
      try {
        const resp = await fetch('/icons/Nezo_Logo_White.png');
        if (resp.ok) {
          const blob = await resp.blob();
          logoData = await new Promise((res) => {
            const reader = new FileReader();
            reader.onload = () => res(reader.result);
            reader.readAsDataURL(blob);
          });
          // Measure natural dimensions to preserve aspect ratio
          await new Promise((res) => {
            const img = new Image();
            img.onload = () => {
              const maxW = 38, maxH = 14;
              const ratio = img.naturalWidth / img.naturalHeight;
              if (ratio > maxW / maxH) {
                logoW = maxW; logoH = maxW / ratio;
              } else {
                logoH = maxH; logoW = maxH * ratio;
              }
              res();
            };
            img.onerror = res;
            img.src = logoData;
          });
        }
      } catch(e) { /* logo unavailable — skip */ }

      /* ── Draw offscreen canvases at 3× for sharp PDF output ── */
      _exportDpr = 3;
      const makeCanvas = (drawFn) => {
        const c = document.createElement('canvas');
        drawFn(c);
        return c;
      };
      const offscreen = {
        mesh:    makeCanvas(c => drawMeshCanvas(c, params.mesh, results.mesh)),
        xsec:    makeCanvas(c => drawXSecCanvas(c, params.bars)),
        walls:   makeCanvas(c => drawWallCanvas(c, params.walls, results.walls)),
        colXSec: makeCanvas(c => drawColXSecCanvas(c, params.cols)),
      };
      _exportDpr = null; // restore

      /* ── Page header (reused on every new page) ── */
      const HEADER_H = 24;
      const drawPageHeader = () => {
        doc.setFillColor(21,145,105);
        doc.rect(0, 0, pw, HEADER_H, 'F');
        // Logo — correctly proportioned
        if (logoData && logoW > 0) {
          const logoY = (HEADER_H - logoH) / 2;
          try { doc.addImage(logoData, 'PNG', mg, logoY, logoW, logoH); } catch(e) {}
        }
        const titleX = logoData && logoW > 0 ? mg + logoW + 5 : mg;
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(13); doc.setFont('helvetica', 'bold');
        doc.text('Nezo Rebar Calculator', titleX, HEADER_H * 0.48);
        doc.setFontSize(7.5); doc.setFont('helvetica', 'normal');
        doc.text('Quantity Estimator — AS/NZS 4671 & AS 3600', titleX, HEADER_H * 0.75);
        doc.text(
          `Generated ${new Date().toLocaleDateString('en-AU',{day:'2-digit',month:'short',year:'numeric'})}`,
          pw - mg, HEADER_H * 0.48, { align: 'right' }
        );
      };
      drawPageHeader();
      y = HEADER_H + 8;

      const ensureSpace = (needed) => {
        if (y + needed > ph - 16) {
          doc.addPage();
          drawPageHeader();
          y = 28;
        }
      };

      const addSection = (title, canvas, rows) => {
        ensureSpace(20);

        /* Section heading */
        doc.setFillColor(240,253,248);
        doc.rect(mg, y, pw-2*mg, 8, 'F');
        doc.setDrawColor(176,238,218);
        doc.rect(mg, y, pw-2*mg, 8, 'S');
        doc.setTextColor(17,116,85); doc.setFontSize(11); doc.setFont('helvetica','bold');
        doc.text(title, mg+3, y+5.5);
        y += 11;

        /* Canvas snapshot */
        if (canvas) {
          try {
            const imgData = canvas.toDataURL('image/png');
            const canvW = pw-2*mg;
            const aspect = canvas.height / canvas.width;
            const canvH = Math.min(65, canvW * aspect);
            ensureSpace(canvH + 4);
            doc.addImage(imgData, 'PNG', mg, y, canvW, canvH);
            y += canvH + 5;
          } catch(e) { /* skip */ }
        }

        /* Table */
        if (rows?.length) {
          const nCols = rows[0].length;
          const usableW = pw - 2*mg;
          const colW = nCols === 4
            ? [usableW*0.28, usableW*0.28, usableW*0.22, usableW*0.22]
            : [usableW*0.45, usableW*0.35, usableW*0.2];

          // Header row
          ensureSpace(6);
          doc.setFillColor(240,253,248);
          doc.rect(mg, y, usableW, 5.5, 'F');
          doc.setDrawColor(176,238,218); doc.rect(mg, y, usableW, 5.5, 'S');
          doc.setFontSize(7); doc.setFont('helvetica','bold');
          doc.setTextColor(107,114,128);
          const hdrs = ['Description','Detail','Value','Unit'].slice(0,nCols);
          let cx = mg+2;
          hdrs.forEach((h,i) => { doc.text(h, cx, y+3.8); cx += colW[i]; });
          y += 5.5;

          // Data rows
          doc.setFont('helvetica','normal'); doc.setFontSize(8);
          rows.forEach((row, ri) => {
            ensureSpace(5.5);
            doc.setFillColor(ri%2===0 ? 255 : 249, ri%2===0 ? 255 : 250, ri%2===0 ? 255 : 251);
            doc.rect(mg, y, usableW, 5, 'F');
            doc.setTextColor(55,65,81);
            let cx2 = mg+2;
            row.forEach((cell, ci) => {
              doc.text(String(cell ?? ''), cx2, y+3.5, { maxWidth: colW[ci]-3 });
              cx2 += colW[ci];
            });
            y += 5;
          });
          y += 6;
        }
      };

      if (selected.mesh) {
        const r = results.mesh;
        addSection('Floor Mesh', offscreen.mesh, [
          ['Mesh Profile', params.mesh.meshId, 'Total Weight', `${fmt(r.totalKg,0)} kg`],
          ['Slab', `${fmt(params.mesh.length,1)} × ${fmt(params.mesh.width,1)} m`, 'Sheet Count', String(r.totalSheets)],
          ['Layers', String(params.mesh.layers), 'Volume', `${fmt5(r.m3)} m³`],
          ['Long. Lap', `${params.mesh.lapLong} mm`, 'Avg Rate', `${fmt(r.avgKgm2,2)} kg/m²`],
          ['Trans. Lap', `${params.mesh.lapTrans} mm`, 'Wastage', `${params.mesh.wastage}%`],
        ]);
      }
      if (selected.bars) {
        const r = results.bars;
        addSection('Bars & Beams', offscreen.xsec, [
          ['Element', params.bars.element==='beam'?'Foundation Beam':'Slab Bars', 'Total Weight', `${fmt(r.total,0)} kg`],
          ['Length', `${params.bars.length} m`, 'Main Bars', `${fmt(r.mainKg,1)} kg`],
          ['Main Bar', barLabel(params.bars.mainDia), 'Stirrups / Ties', `${fmt(r.tieKg,1)} kg`],
          ['Cover', `${params.bars.coverMm} mm`, 'Volume', `${fmt5(r.m3)} m³`],
        ]);
      }
      if (selected.walls) {
        const r = results.walls;
        addSection('Concrete Walls', offscreen.walls, [
          ['Type', params.walls.type==='insitu'?'In-Situ':'Blockwork', 'Total Weight', `${fmt(r.total,0)} kg`],
          ['Size', `${fmt(params.walls.length,1)} × ${fmt(params.walls.height,1)} m`, 'Vertical', `${fmt(r.vertKg,1)} kg`],
          ['Vert Bar', `${barLabel(params.walls.vertDia)} @ ${params.walls.vertSp}mm`, 'Horizontal', `${fmt(r.horizKg,1)} kg`],
          ['Horiz Bar', `${barLabel(params.walls.horizDia)} @ ${params.walls.horizSp}mm`, 'Starters', `${fmt(r.startKg,1)} kg`],
          ['Starter Bar', `${barLabel(params.walls.starterDia)} @ ${params.walls.starterSp}mm`, 'Volume', `${fmt5(r.m3)} m³`],
        ]);
      }
      if (selected.cols) {
        const r = results.cols;
        addSection('Columns & Piers', offscreen.colXSec, [
          ['Shape', params.cols.shape==='rect'?`${params.cols.bMm}×${params.cols.dMm}mm`:`⌀${params.cols.diamMm}mm`, 'Total Weight', `${fmt(r.total,0)} kg`],
          ['Height', `${params.cols.length} m`, 'Main Bars', `${fmt(r.mainKg,1)} kg`],
          ['Main Bars', `${params.cols.nBars} × ${barLabel(params.cols.mainDia)}`, 'Ligatures', `${fmt(r.ligKg,1)} kg`],
          ['Ligature', `${fitLabel(params.cols.ligDia)} @ ${params.cols.ligSp}mm`, 'Volume', `${fmt5(r.m3)} m³`],
        ]);
      }

      /* ── Footer on every page ── */
      const pageCount = doc.internal.getNumberOfPages();
      for (let i=1; i<=pageCount; i++) {
        doc.setPage(i);
        doc.setDrawColor(176,238,218); doc.line(mg, ph-10, pw-mg, ph-10);
        doc.setFontSize(7); doc.setTextColor(156,163,175); doc.setFont('helvetica','normal');
        doc.text('Nezo Rebar Calculator — For quantity estimating purposes only. Not a substitute for structural engineering design.', mg, ph-6);
        doc.text(`Page ${i} of ${pageCount}`, pw-mg, ph-6, {align:'right'});
      }

      doc.save(`nezo-rebar-${new Date().toISOString().slice(0,10)}.pdf`);
    } catch(e) {
      alert('Export failed. Ensure jsPDF is installed: npm install jspdf');
      console.error(e);
    }
    setExporting(false);
    onClose();
  };

  return (
    <div className="rc-modal-overlay" onClick={onClose}>
      <div className="rc-modal" onClick={e=>e.stopPropagation()}>
        <div className="rc-modal__header">
          <div className="rc-modal__title">
            <span>📄</span> Export to PDF
          </div>
          <button type="button" className="rc-modal__close" onClick={onClose}>✕</button>
        </div>
        <div className="rc-modal__body">
          <p className="rc-modal__desc">Select which tabs to include in the exported PDF report.</p>
          <div className="rc-modal__select-all">
            <label className="rc-checkbox">
              <input type="checkbox" checked={allOn} onChange={e=>toggleAll(e.target.checked)} ref={el=>{if(el) el.indeterminate=!allOn&&!noneOn;}}/>
              <span>{allOn ? 'Deselect All' : 'Select All'}</span>
            </label>
          </div>
          <div className="rc-modal__tabs">
            {TAB_EXPORT_DEFS.map(t=>(
              <label key={t.id} className={`rc-checkbox-card${selected[t.id]?' active':''}`}>
                <input type="checkbox" checked={selected[t.id]} onChange={e=>setSelected(s=>({...s,[t.id]:e.target.checked}))}/>
                <span className="rc-checkbox-card__label">{t.label}</span>
                {selected[t.id] && <span className="rc-checkbox-card__tick">✓</span>}
              </label>
            ))}
          </div>
        </div>
        <div className="rc-modal__footer">
          <button type="button" className="rc-modal__cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="rc-modal__export" onClick={handleExport} disabled={exporting||noneOn}>
            {exporting ? '⏳ Generating…' : '⬇ Download PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   REUSABLE INPUT COMPONENTS
══════════════════════════════════════════════════════════════════ */

function ToggleGroup({ value, onChange, options }) {
  return (
    <div className="rc-toggle-group">
      {options.map(({ val, label }) => (
        <button key={val} type="button" className={`rc-toggle-btn${value===val?' active':''}`} onClick={()=>onChange(val)}>{label}</button>
      ))}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="rc-field">
      <label className="rc-field__label">{label}</label>
      {children}
      {hint && <span className="rc-field__hint">{hint}</span>}
    </div>
  );
}

function NumInput({ value, onChange, min, max, step=1, unit }) {
  const clamp = (v) => {
    let n = parseFloat(parseFloat(v).toFixed(10));
    if (!isFinite(n)) n = min ?? 0;
    if (min !== undefined) n = Math.max(min, n);
    if (max !== undefined) n = Math.min(max, n);
    return n;
  };
  const handleStep = (dir) => onChange(clamp(value + dir * step));
  return (
    <div className="rc-num-wrap">
      <button type="button" className="rc-num-btn" onClick={()=>handleStep(-1)} aria-label="decrease">−</button>
      <input type="number" className="rc-num-input" value={value} min={min} max={max} step={step}
        onChange={e=>onChange(clamp(e.target.value))} />
      <button type="button" className="rc-num-btn rc-num-btn--plus" onClick={()=>handleStep(1)} aria-label="increase">+</button>
      {unit && <span className="rc-num-unit">{unit}</span>}
    </div>
  );
}

function SelectInput({ value, onChange, options }) {
  return (
    <select className="rc-select" value={value} onChange={e=>onChange(e.target.value)}>
      {options.map(({ val, label }) => <option key={val} value={val}>{label}</option>)}
    </select>
  );
}

function ResultCard({ label, value, unit, accent }) {
  return (
    <div className={`rc-result-card${accent?' rc-result-card--accent':''}`}>
      <div className="rc-result-label">{label}</div>
      <div className="rc-result-value">{value}</div>
      {unit && <div className="rc-result-unit">{unit}</div>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */

const TAB_DEFS = [
  { id:'mesh',  label:'Floor Mesh',           icon:'⬛' },
  { id:'bars',  label:'Bars & Beams',          icon:'📐' },
  { id:'walls', label:'Concrete Walls',        icon:'🧱' },
  { id:'cols',  label:'Columns & Piers',       icon:'🏛️' },
];

const BAR_OPTIONS = Object.entries(BAR_DATA).map(([d,v])=>({ val:String(d), label:v.label }));
const FIT_OPTIONS = Object.entries(FITMENT_DATA).map(([d,v])=>({ val:String(d), label:v.label }));
const SL_OPTIONS  = Object.keys(MESH_DATA).filter(k=>MESH_DATA[k].series==='SL').map(k=>({ val:k, label:k }));
const RL_OPTIONS  = Object.keys(MESH_DATA).filter(k=>MESH_DATA[k].series==='RL').map(k=>({ val:k, label:k }));

export default function RebarCalculator() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'mesh');
  const [showExport, setShowExport] = useState(false);

  /* ── Tab 1: Mesh ── */
  const [mesh, setMesh] = useState({
    type:'square', meshId:'SL82', length:12, width:8, layers:1, lapLong:300, lapTrans:150, wastage:5,
  });
  const meshCanvasRef  = useRef(null);

  /* ── Tab 2: Bars ── */
  const [bars, setBars] = useState({
    element:'beam', length:8, widthMm:400, depthMm:600, coverMm:40,
    mainDia:20, botCount:3, topCount:2, lapMm:600, lapsCount:1,
    tieDia:10, tieSp:200, tieEndSp:100, tieEndLen:500, slabSp:200, slabSpan:6,
  });
  const [barsView, setBarsView] = useState('xsec');
  const xsecCanvasRef = useRef(null);
  const longCanvasRef = useRef(null);

  /* ── Tab 3: Walls ── */
  const [walls, setWalls] = useState({
    type:'insitu', length:8, height:3, thickMm:200, coverMm:40,
    vertDia:16, vertSp:200, vertFaces:2, vertLapMm:500, vertLapCount:1,
    horizDia:12, horizSp:200, horizLapMm:300,
    starterDia:16, starterSp:200, starterLenMm:600, starterFaces:1,
  });
  const wallsCanvasRef = useRef(null);

  /* ── Tab 4: Columns ── */
  const [cols, setCols] = useState({
    shape:'rect', length:4, bMm:400, dMm:400, diamMm:500, coverMm:40,
    mainDia:20, nBars:8, ligDia:10, ligSp:200, ligEndSp:100, ligEndLen:500, lapMm:600, lapsCount:1,
  });
  const [colView, setColView] = useState('xsec');
  const colXSecCanvasRef = useRef(null);
  const colElevCanvasRef = useRef(null);

  /* ── Canvas drawing effects ── */
  const drawMesh = useCallback(() => {
    const res = calcMeshQuantities(mesh);
    drawMeshCanvas(meshCanvasRef.current, mesh, res);
  }, [mesh]);

  const drawBars = useCallback(() => {
    drawXSecCanvas(xsecCanvasRef.current, bars);
    drawLongCanvas(longCanvasRef.current, bars);
  }, [bars]);

  const drawWalls = useCallback(() => {
    const res = calcWallsQuantities(walls);
    drawWallCanvas(wallsCanvasRef.current, walls, res);
  }, [walls]);

  const drawCols = useCallback(() => {
    const res = calcColsQuantities(cols);
    drawColXSecCanvas(colXSecCanvasRef.current, cols);
    drawColElevCanvas(colElevCanvasRef.current, cols, res);
  }, [cols]);

  useEffect(() => { if(activeTab==='mesh')  drawMesh();  }, [mesh,  activeTab, drawMesh]);
  useEffect(() => { if(activeTab==='bars')  drawBars();  }, [bars,  activeTab, barsView, drawBars]);
  useEffect(() => { if(activeTab==='walls') drawWalls(); }, [walls, activeTab, drawWalls]);
  useEffect(() => { if(activeTab==='cols')  drawCols();  }, [cols,  activeTab, colView,  drawCols]);

  /* ── Computed results ── */
  const meshRes  = calcMeshQuantities(mesh);
  const barsRes  = calcBarsQuantities(bars);
  const wallsRes = calcWallsQuantities(walls);
  const colsRes  = calcColsQuantities(cols);

  const setM = (k) => (v) => setMesh(p=>({...p,[k]:v}));
  const setB = (k) => (v) => setBars(p=>({...p,[k]:v}));
  const setW = (k) => (v) => setWalls(p=>({...p,[k]:v}));
  const setC = (k) => (v) => setCols(p=>({...p,[k]:v}));

  /* ── Mesh type → auto-pick first profile ── */
  const handleMeshType = (val) => {
    setMesh(p=>({...p, type:val, meshId: val==='square' ? 'SL82' : 'RL818'}));
  };

  /* ════════════════════════════════ RENDER ════════════════════════════════ */
  return (
    <div className="rc-root">
      {/* Export Modal */}
      {showExport && (
        <ExportModal
          onClose={()=>setShowExport(false)}
          canvases={{ mesh:meshCanvasRef, xsec:xsecCanvasRef, walls:wallsCanvasRef, colXSec:colXSecCanvasRef }}
          results={{ mesh:meshRes, bars:barsRes, walls:wallsRes, cols:colsRes }}
          params={{ mesh, bars, walls, cols }}
        />
      )}

      {/* Header */}
      <div className="rc-header">
        <div className="rc-header__text">
          <h1>Rebar Calculator</h1>
          <p>Quantity estimator — AS/NZS 4671 &amp; AS 3600 · For estimating purposes only</p>
        </div>
        <div className="rc-header__actions">
          <button type="button" className="rc-export-btn" onClick={()=>setShowExport(true)}>
            📄 Export PDF
          </button>
          <div className="rc-live-badge"><span className="rc-live-dot"/>LIVE</div>
        </div>
      </div>

      {/* Tab Nav */}
      <div className="rc-tab-nav">
        {TAB_DEFS.map(t=>(
          <button key={t.id} type="button"
            className={`rc-tab-btn${activeTab===t.id?' active':''}`}
            onClick={()=>setActiveTab(t.id)}>
            <span>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      {/* ══ TAB 1: FLOOR MESH ══════════════════════════════════════════════ */}
      {activeTab==='mesh' && (
        <div className="rc-panel-layout">
          {/* Inputs */}
          <div className="rc-inputs-col">
            <div className="rc-card">
              <div className="rc-card-header"><span className="rc-card-icon">⬛</span><h2>Mesh Profile</h2></div>
              <div className="rc-card-body">
                <Field label="Series">
                  <ToggleGroup value={mesh.type} onChange={handleMeshType}
                    options={[{val:'square',label:'SL Square'},{val:'rect',label:'RL Rectangular'}]}/>
                </Field>
                {mesh.type==='square' && (
                  <Field label="SL Profile">
                    <SelectInput value={mesh.meshId} onChange={setM('meshId')} options={SL_OPTIONS}/>
                  </Field>
                )}
                {mesh.type==='rect' && (
                  <Field label="RL Profile">
                    <SelectInput value={mesh.meshId} onChange={setM('meshId')} options={RL_OPTIONS}/>
                  </Field>
                )}
                {meshRes.mesh && (
                  <div className="rc-spec-explain">
                    <div className="rc-spec-explain__title">
                      <strong>{meshRes.meshId}</strong>
                      <span className="rc-spec-explain__badge">InfraBuild ONEMESH®</span>
                    </div>
                    <div className="rc-spec-explain__wire">{meshRes.mesh.wire}</div>
                    {meshRes.mesh.notes && (
                      <div className="rc-spec-explain__notes">
                        {meshRes.mesh.notes.split('·').map((part, i) => (
                          <div key={i} className="rc-spec-explain__note-line">
                            <span className="rc-spec-explain__bullet">›</span>
                            <span>{part.trim()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="rc-spec-explain__footer">
                      Sheet: 6.0 m × 2.4 m &nbsp;|&nbsp; Mass: <strong>{meshRes.mesh.kgm2} kg/m²</strong>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="rc-card">
              <div className="rc-card-header"><span className="rc-card-icon">📐</span><h2>Slab Dimensions</h2></div>
              <div className="rc-card-body rc-card-body--grid">
                <Field label="Length"><NumInput value={mesh.length} onChange={setM('length')} min={0.5} step={0.5} unit="m"/></Field>
                <Field label="Width"><NumInput value={mesh.width} onChange={setM('width')} min={0.5} step={0.5} unit="m"/></Field>
                <Field label="Layers">
                  <ToggleGroup value={String(mesh.layers)} onChange={v=>setM('layers')(parseInt(v))}
                    options={[{val:'1',label:'Single'},{val:'2',label:'Double'}]}/>
                </Field>
              </div>
            </div>

            <div className="rc-card">
              <div className="rc-card-header"><span className="rc-card-icon">🔗</span><h2>Laps &amp; Wastage</h2></div>
              <div className="rc-card-body rc-card-body--grid">
                <Field label="Long. Lap"><NumInput value={mesh.lapLong} onChange={setM('lapLong')} min={0} step={25} unit="mm"/></Field>
                <Field label="Trans. Lap"><NumInput value={mesh.lapTrans} onChange={setM('lapTrans')} min={0} step={25} unit="mm"/></Field>
                <Field label="Wastage"><NumInput value={mesh.wastage} onChange={setM('wastage')} min={0} max={25} step={0.5} unit="%"/></Field>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="rc-results-col">
            <div className="rc-card rc-viz-card">
              <div className="rc-card-header">
                <span className="rc-card-icon">🗺️</span><h2>Floor Plan</h2>
                <span className="rc-live-badge sm"><span className="rc-live-dot"/>LIVE</span>
              </div>
              <PanZoomCanvas>
                <canvas ref={meshCanvasRef} style={{width:'100%',height:'auto',display:'block'}}/>
              </PanZoomCanvas>
            </div>

            <div className="rc-results-grid">
              <ResultCard accent label="Total Weight" value={fmt(meshRes.totalKg,0)} unit="kg"/>
              <ResultCard label="Sheet Count" value={meshRes.totalSheets||'—'} unit={`sheets (${meshRes.nL||0}×${meshRes.nW||0})`}/>
              <ResultCard label="Floor Area" value={fmt((mesh.length||0) * (mesh.width||0), 2)} unit="m² net"/>
              <ResultCard label="Volume" value={fmt5(meshRes.m3)} unit="m³"/>
              <ResultCard label="Profile Rate" value={fmt(meshRes.mesh?.kgm2||0,2)} unit="kg/m²"/>
              <ResultCard label="Avg. Rate" value={fmt(meshRes.avgKgm2||0,2)} unit="kg/m² total"/>
              <ResultCard label="m³ / m² Floor"
                value={fmt5(meshRes.m3 > 0 && mesh.length > 0 && mesh.width > 0
                  ? meshRes.m3 / (mesh.length * mesh.width)
                  : 0)}
                unit="m³/m² incl. laps & waste"/>
            </div>

            {meshRes.rows.length > 0 && (
              <div className="rc-card rc-summary-card">
                <div className="rc-card-header"><span className="rc-card-icon">📋</span><h2>Quantity Summary</h2></div>
                <div className="rc-table-wrap">
                  <table className="rc-table">
                    <thead><tr><th>Layer</th><th>Profile</th><th>Net Area</th><th>Sheets</th><th>Rate</th><th>Weight</th></tr></thead>
                    <tbody>
                      {meshRes.rows.map((r,i)=>(
                        <tr key={i}><td>{r.layer}</td><td><code>{r.profile}</code></td><td>{r.netArea} m²</td><td className="num">{r.sheets}</td><td>{r.rate} kg/m²</td><td className="num">{r.kg} kg</td></tr>
                      ))}
                      {meshRes.lapArea>0.001&&<tr className="sub"><td colSpan={5}>+ Lap zones ({fmt(meshRes.lapArea*meshRes.layers,2)} m²)</td><td className="num">+{fmt(meshRes.kgAllLayers-meshRes.kgNetPerLayer*meshRes.layers,1)} kg</td></tr>}
                      {meshRes.wastageD>0&&<tr className="sub"><td colSpan={5}>+ Site wastage ({(meshRes.wastageD*100).toFixed(0)}%)</td><td className="num">+{fmt(meshRes.kgWastage,1)} kg</td></tr>}
                    </tbody>
                    <tfoot><tr className="total"><td colSpan={4}><strong>TOTAL — {meshRes.totalSheets} sheets × {meshRes.layers} layer{meshRes.layers>1?'s':''}</strong></td><td/><td className="num"><strong>{fmt(meshRes.totalKg,0)} kg</strong></td></tr></tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ TAB 2: BARS ═══════════════════════════════════════════════════ */}
      {activeTab==='bars' && (
        <div className="rc-panel-layout">
          <div className="rc-inputs-col">
            <div className="rc-card">
              <div className="rc-card-header"><span className="rc-card-icon">📐</span><h2>Element Type</h2></div>
              <div className="rc-card-body">
                <Field label="Type">
                  <ToggleGroup value={bars.element} onChange={setB('element')}
                    options={[{val:'beam',label:'Foundation Beam'},{val:'slab',label:'Slab Bars'}]}/>
                </Field>
                <div className="rc-card-body--grid">
                  <Field label="Length"><NumInput value={bars.length} onChange={setB('length')} min={0.5} step={0.5} unit="m"/></Field>
                  {bars.element==='beam' && <>
                    <Field label="Width (B)"><NumInput value={bars.widthMm} onChange={setB('widthMm')} min={100} step={25} unit="mm"/></Field>
                    <Field label="Depth (D)"><NumInput value={bars.depthMm} onChange={setB('depthMm')} min={100} step={25} unit="mm"/></Field>
                  </>}
                  {bars.element==='slab' && <>
                    <Field label="Slab Depth"><NumInput value={bars.depthMm} onChange={setB('depthMm')} min={100} step={25} unit="mm"/></Field>
                    <Field label="Slab Span"><NumInput value={bars.slabSpan} onChange={setB('slabSpan')} min={0.5} step={0.5} unit="m"/></Field>
                  </>}
                  <Field label="Cover"><NumInput value={bars.coverMm} onChange={setB('coverMm')} min={20} max={100} step={5} unit="mm"/></Field>
                </div>
              </div>
            </div>

            <div className="rc-card">
              <div className="rc-card-header"><span className="rc-card-icon">🔩</span><h2>Main Bars</h2></div>
              <div className="rc-card-body rc-card-body--grid">
                <Field label="Bar Size"><SelectInput value={String(bars.mainDia)} onChange={v=>setB('mainDia')(parseInt(v))} options={BAR_OPTIONS}/></Field>
                {bars.element==='beam' && <>
                  <Field label="Bottom Bars"><NumInput value={bars.botCount} onChange={setB('botCount')} min={0} max={12} step={1}/></Field>
                  <Field label="Top Bars"><NumInput value={bars.topCount} onChange={setB('topCount')} min={0} max={8} step={1}/></Field>
                </>}
                {bars.element==='slab' && (
                  <Field label="Bar Spacing"><NumInput value={bars.slabSp} onChange={setB('slabSp')} min={75} max={400} step={25} unit="mm"/></Field>
                )}
                <Field label="Lap Length"><NumInput value={bars.lapMm} onChange={setB('lapMm')} min={0} step={50} unit="mm"/></Field>
                <Field label="No. of Laps"><NumInput value={bars.lapsCount} onChange={setB('lapsCount')} min={0} max={10} step={1}/></Field>
              </div>
            </div>

            {bars.element==='beam' && (
              <div className="rc-card">
                <div className="rc-card-header"><span className="rc-card-icon">🔗</span><h2>Stirrups</h2></div>
                <div className="rc-card-body rc-card-body--grid">
                  <Field label="Tie Diameter"><SelectInput value={String(bars.tieDia)} onChange={v=>setB('tieDia')(parseInt(v))} options={FIT_OPTIONS}/></Field>
                  <Field label="End Zone Len."><NumInput value={bars.tieEndLen} onChange={setB('tieEndLen')} min={0} step={50} unit="mm"/></Field>
                  <Field label="End Zone Sp."><NumInput value={bars.tieEndSp} onChange={setB('tieEndSp')} min={50} max={200} step={25} unit="mm"/></Field>
                  <Field label="Mid Zone Sp."><NumInput value={bars.tieSp} onChange={setB('tieSp')} min={100} max={400} step={25} unit="mm"/></Field>
                </div>
              </div>
            )}
          </div>

          <div className="rc-results-col">
            <div className="rc-card rc-viz-card">
              <div className="rc-card-header">
                <span className="rc-card-icon">✏️</span>
                <h2>{barsView==='xsec'?'Cross Section':'Longitudinal Section'}</h2>
                <div className="rc-view-toggle">
                  <button type="button" className={`rc-view-btn${barsView==='xsec'?' active':''}`} onClick={()=>setBarsView('xsec')}>Cross Section</button>
                  <button type="button" className={`rc-view-btn${barsView==='long'?' active':''}`} onClick={()=>setBarsView('long')}>Elevation</button>
                </div>
                <span className="rc-live-badge sm"><span className="rc-live-dot"/>LIVE</span>
              </div>
              <PanZoomCanvas>
                <canvas ref={xsecCanvasRef} style={{width:'100%',height:'auto',display:barsView==='xsec'?'block':'none'}}/>
                <canvas ref={longCanvasRef}  style={{width:'100%',height:'auto',display:barsView==='long'?'block':'none'}}/>
              </PanZoomCanvas>
            </div>

            <div className="rc-results-grid">
              <ResultCard accent label="Total Weight" value={fmt(barsRes.total,0)} unit="kg"/>
              <ResultCard label="Main Bars" value={fmt(barsRes.mainKg,1)} unit="kg"/>
              {bars.element==='beam' && <ResultCard label="Stirrups" value={fmt(barsRes.tieKg,1)} unit="kg"/>}
              <ResultCard label="Volume" value={fmt5(barsRes.m3)} unit="m³"/>
            </div>

            <div className="rc-card rc-summary-card">
              <div className="rc-card-header"><span className="rc-card-icon">📋</span><h2>Bar Schedule</h2></div>
              <div className="rc-table-wrap">
                <table className="rc-table">
                  <thead><tr><th>Bar</th><th>Description</th><th>No.</th><th>L ea.</th><th>Total (kg)</th></tr></thead>
                  <tbody>
                    {barsRes.schedRows.map((r,i)=>(
                      <tr key={i}><td><code>{r.bar}</code></td><td>{r.desc}</td><td className="num">{r.n}</td><td className="num">{fmt(r.lenEa,2)} m</td><td className="num">{fmt(r.kg,1)}</td></tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="total"><td colSpan={4}><strong>TOTAL</strong></td><td className="num"><strong>{fmt(barsRes.total,0)} kg</strong><br/><small>{fmt5(barsRes.m3)} m³</small></td></tr></tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ TAB 3: WALLS ══════════════════════════════════════════════════ */}
      {activeTab==='walls' && (
        <div className="rc-panel-layout">
          <div className="rc-inputs-col">
            <div className="rc-card">
              <div className="rc-card-header"><span className="rc-card-icon">🧱</span><h2>Wall Geometry</h2></div>
              <div className="rc-card-body">
                <Field label="Wall Type">
                  <ToggleGroup value={walls.type} onChange={setW('type')}
                    options={[{val:'insitu',label:'In-Situ'},{val:'block',label:'Blockwork'}]}/>
                </Field>
                <div className="rc-card-body--grid">
                  <Field label="Length"><NumInput value={walls.length} onChange={setW('length')} min={0.5} step={0.5} unit="m"/></Field>
                  <Field label="Height"><NumInput value={walls.height} onChange={setW('height')} min={0.5} step={0.25} unit="m"/></Field>
                  <Field label="Thickness"><NumInput value={walls.thickMm} onChange={setW('thickMm')} min={100} step={25} unit="mm"/></Field>
                  <Field label="Cover"><NumInput value={walls.coverMm} onChange={setW('coverMm')} min={20} step={5} unit="mm"/></Field>
                </div>
              </div>
            </div>

            <div className="rc-card">
              <div className="rc-card-header"><span className="rc-card-icon">↕️</span><h2>Vertical Bars</h2></div>
              <div className="rc-card-body rc-card-body--grid">
                <Field label="Bar Size"><SelectInput value={String(walls.vertDia)} onChange={v=>setW('vertDia')(parseInt(v))} options={BAR_OPTIONS}/></Field>
                <Field label="Spacing"><NumInput value={walls.vertSp} onChange={setW('vertSp')} min={75} max={400} step={25} unit="mm"/></Field>
                <Field label="Faces">
                  <ToggleGroup value={String(walls.vertFaces)} onChange={v=>setW('vertFaces')(parseInt(v))} options={[{val:'1',label:'1 Face'},{val:'2',label:'2 Faces'}]}/>
                </Field>
                <Field label="Lap Length"><NumInput value={walls.vertLapMm} onChange={setW('vertLapMm')} min={0} step={50} unit="mm"/></Field>
                <Field label="No. Laps"><NumInput value={walls.vertLapCount} onChange={setW('vertLapCount')} min={0} step={1}/></Field>
              </div>
            </div>

            <div className="rc-card">
              <div className="rc-card-header"><span className="rc-card-icon">↔️</span><h2>Horizontal Bars</h2></div>
              <div className="rc-card-body rc-card-body--grid">
                <Field label="Bar Size"><SelectInput value={String(walls.horizDia)} onChange={v=>setW('horizDia')(parseInt(v))} options={BAR_OPTIONS}/></Field>
                <Field label="Spacing"><NumInput value={walls.horizSp} onChange={setW('horizSp')} min={75} max={400} step={25} unit="mm"/></Field>
                <Field label="Lap"><NumInput value={walls.horizLapMm} onChange={setW('horizLapMm')} min={0} step={25} unit="mm"/></Field>
              </div>
            </div>

            <div className="rc-card">
              <div className="rc-card-header"><span className="rc-card-icon">📌</span><h2>Starter Bars</h2></div>
              <div className="rc-card-body rc-card-body--grid">
                <Field label="Bar Size"><SelectInput value={String(walls.starterDia)} onChange={v=>setW('starterDia')(parseInt(v))} options={BAR_OPTIONS}/></Field>
                <Field label="Spacing"><NumInput value={walls.starterSp} onChange={setW('starterSp')} min={75} max={400} step={25} unit="mm"/></Field>
                <Field label="Length"><NumInput value={walls.starterLenMm} onChange={setW('starterLenMm')} min={100} step={50} unit="mm"/></Field>
                <Field label="Faces">
                  <ToggleGroup value={String(walls.starterFaces)} onChange={v=>setW('starterFaces')(parseInt(v))} options={[{val:'1',label:'1 Face'},{val:'2',label:'2 Faces'}]}/>
                </Field>
              </div>
            </div>
          </div>

          <div className="rc-results-col">
            <div className="rc-card rc-viz-card">
              <div className="rc-card-header">
                <span className="rc-card-icon">🏗️</span><h2>Wall Elevation</h2>
                <span className="rc-live-badge sm"><span className="rc-live-dot"/>LIVE</span>
              </div>
              <PanZoomCanvas>
                <canvas ref={wallsCanvasRef} style={{width:'100%',height:'auto',display:'block'}}/>
              </PanZoomCanvas>
            </div>

            <div className="rc-results-grid">
              <ResultCard accent label="Total Weight" value={fmt(wallsRes.total,0)} unit="kg"/>
              <ResultCard label="Vertical Bars" value={fmt(wallsRes.vertKg,1)} unit="kg"/>
              <ResultCard label="Horizontal Bars" value={fmt(wallsRes.horizKg,1)} unit="kg"/>
              <ResultCard label="Starter Bars" value={fmt(wallsRes.startKg,1)} unit="kg"/>
              <ResultCard label="Volume" value={fmt5(wallsRes.m3)} unit="m³"/>
              <ResultCard label="Rate" value={fmt(wallsRes.kgm2,2)} unit="kg/m²"/>
            </div>

            <div className="rc-card rc-summary-card">
              <div className="rc-card-header"><span className="rc-card-icon">📋</span><h2>Bar Schedule</h2></div>
              <div className="rc-table-wrap">
                <table className="rc-table">
                  <thead><tr><th>Bar</th><th>Description</th><th>No.</th><th>L ea.</th><th>Faces</th><th>Total (kg)</th></tr></thead>
                  <tbody>
                    <tr><td><code>{barLabel(walls.vertDia)}</code></td><td>Vertical @ {walls.vertSp}mm crs</td><td className="num">{wallsRes.nVert}</td><td className="num">{fmt(wallsRes.vertLen,2)} m</td><td className="num">{walls.vertFaces}</td><td className="num">{fmt(wallsRes.vertKg,1)}</td></tr>
                    <tr><td><code>{barLabel(walls.horizDia)}</code></td><td>Horizontal @ {walls.horizSp}mm crs</td><td className="num">{wallsRes.nHoriz}</td><td className="num">{fmt(wallsRes.horizLen,2)} m</td><td className="num">{walls.vertFaces}</td><td className="num">{fmt(wallsRes.horizKg,1)}</td></tr>
                    <tr><td><code>{barLabel(walls.starterDia)}</code></td><td>Starters @ {walls.starterSp}mm — {walls.starterLenMm}mm</td><td className="num">{wallsRes.nStart}</td><td className="num">{fmt(wallsRes.startLen,2)} m</td><td className="num">{walls.starterFaces}</td><td className="num">{fmt(wallsRes.startKg,1)}</td></tr>
                  </tbody>
                  <tfoot><tr className="total"><td colSpan={5}><strong>TOTAL — {fmt(walls.length,1)}m × {fmt(walls.height,1)}m wall</strong></td><td className="num"><strong>{fmt(wallsRes.total,0)} kg</strong></td></tr></tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ TAB 4: COLUMNS ════════════════════════════════════════════════ */}
      {activeTab==='cols' && (
        <div className="rc-panel-layout">
          <div className="rc-inputs-col">
            <div className="rc-card">
              <div className="rc-card-header"><span className="rc-card-icon">🏛️</span><h2>Column Geometry</h2></div>
              <div className="rc-card-body">
                <Field label="Shape">
                  <ToggleGroup value={cols.shape} onChange={setC('shape')}
                    options={[{val:'rect',label:'Rectangular'},{val:'circ',label:'Circular'}]}/>
                </Field>
                <div className="rc-card-body--grid">
                  <Field label="Height / Length"><NumInput value={cols.length} onChange={setC('length')} min={0.5} step={0.25} unit="m"/></Field>
                  <Field label="Cover"><NumInput value={cols.coverMm} onChange={setC('coverMm')} min={20} step={5} unit="mm"/></Field>
                  {cols.shape==='rect' && <>
                    <Field label="Width (B)"><NumInput value={cols.bMm} onChange={setC('bMm')} min={200} step={25} unit="mm"/></Field>
                    <Field label="Depth (D)"><NumInput value={cols.dMm} onChange={setC('dMm')} min={200} step={25} unit="mm"/></Field>
                  </>}
                  {cols.shape==='circ' && (
                    <Field label="Diameter"><NumInput value={cols.diamMm} onChange={setC('diamMm')} min={200} step={25} unit="mm"/></Field>
                  )}
                </div>
              </div>
            </div>

            <div className="rc-card">
              <div className="rc-card-header"><span className="rc-card-icon">🔩</span><h2>Main Bars</h2></div>
              <div className="rc-card-body rc-card-body--grid">
                <Field label="Bar Size"><SelectInput value={String(cols.mainDia)} onChange={v=>setC('mainDia')(parseInt(v))} options={BAR_OPTIONS}/></Field>
                <Field label="No. Bars"><NumInput value={cols.nBars} onChange={setC('nBars')} min={4} max={24} step={1}/></Field>
                <Field label="Lap Length"><NumInput value={cols.lapMm} onChange={setC('lapMm')} min={0} step={50} unit="mm"/></Field>
                <Field label="No. Laps"><NumInput value={cols.lapsCount} onChange={setC('lapsCount')} min={0} step={1}/></Field>
              </div>
            </div>

            <div className="rc-card">
              <div className="rc-card-header"><span className="rc-card-icon">🔗</span><h2>Ligatures</h2></div>
              <div className="rc-card-body rc-card-body--grid">
                <Field label="Lig. Size"><SelectInput value={String(cols.ligDia)} onChange={v=>setC('ligDia')(parseInt(v))} options={FIT_OPTIONS}/></Field>
                <Field label="End Zone Len."><NumInput value={cols.ligEndLen} onChange={setC('ligEndLen')} min={0} step={50} unit="mm"/></Field>
                <Field label="End Zone Sp."><NumInput value={cols.ligEndSp} onChange={setC('ligEndSp')} min={50} max={200} step={25} unit="mm"/></Field>
                <Field label="Mid Zone Sp."><NumInput value={cols.ligSp} onChange={setC('ligSp')} min={100} max={400} step={25} unit="mm"/></Field>
              </div>
            </div>
          </div>

          <div className="rc-results-col">
            <div className="rc-card rc-viz-card">
              <div className="rc-card-header">
                <span className="rc-card-icon">📐</span>
                <h2>{colView==='xsec'?'Cross Section':'Column Elevation'}</h2>
                <div className="rc-view-toggle">
                  <button type="button" className={`rc-view-btn${colView==='xsec'?' active':''}`} onClick={()=>setColView('xsec')}>Cross Section</button>
                  <button type="button" className={`rc-view-btn${colView==='long'?' active':''}`} onClick={()=>setColView('long')}>Elevation</button>
                </div>
                <span className="rc-live-badge sm"><span className="rc-live-dot"/>LIVE</span>
              </div>
              <PanZoomCanvas>
                <canvas ref={colXSecCanvasRef} style={{width:'100%',height:'auto',display:colView==='xsec'?'block':'none'}}/>
                <canvas ref={colElevCanvasRef}  style={{width:'100%',height:'auto',display:colView==='long'?'block':'none'}}/>
              </PanZoomCanvas>
            </div>

            <div className="rc-results-grid">
              <ResultCard accent label="Total Weight" value={fmt(colsRes.total,0)} unit="kg"/>
              <ResultCard label="Main Bars" value={fmt(colsRes.mainKg,1)} unit="kg"/>
              <ResultCard label="Ligatures" value={fmt(colsRes.ligKg,1)} unit="kg"/>
              <ResultCard label="Volume" value={fmt5(colsRes.m3)} unit="m³"/>
            </div>

            <div className="rc-card rc-summary-card">
              <div className="rc-card-header"><span className="rc-card-icon">📋</span><h2>Bar Schedule</h2></div>
              <div className="rc-table-wrap">
                <table className="rc-table">
                  <thead><tr><th>Bar</th><th>Description</th><th>No.</th><th>L ea.</th><th>Total (kg)</th></tr></thead>
                  <tbody>
                    <tr><td><code>{barLabel(cols.mainDia)}</code></td><td>Main bars ({cols.nBars} No.)</td><td className="num">{cols.nBars}</td><td className="num">{fmt(colsRes.mainLen,2)} m</td><td className="num">{fmt(colsRes.mainKg,1)}</td></tr>
                    <tr><td><code>{fitLabel(cols.ligDia)}</code></td><td>Ligatures — {cols.ligEndSp}mm EZ / {cols.ligSp}mm crs ({colsRes.totalLigs} No.)</td><td className="num">{colsRes.totalLigs}</td><td className="num">{fmt(colsRes.ligPerim,2)} m</td><td className="num">{fmt(colsRes.ligKg,1)}</td></tr>
                  </tbody>
                  <tfoot><tr className="total"><td colSpan={4}><strong>TOTAL</strong></td><td className="num"><strong>{fmt(colsRes.total,0)} kg</strong><br/><small>{fmt5(colsRes.m3)} m³</small></td></tr></tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="rc-disclaimer">
        ⚠️ For <strong>quantity estimating purposes only</strong>. Not a substitute for structural engineering design. Always consult a qualified structural engineer. AS/NZS 4671 bar masses · InfraBuild ONEMESH® mesh data.
      </div>
    </div>
  );
}